import React, { useEffect, useState, useRef, useMemo } from "react";
import {
  KeyboardAvoidingView,
  ScrollView,
  StyleSheet,
  View,
  Text,
  TouchableOpacity,
  Platform,
  SafeAreaView,
  Image,
  Alert,
  TextInput,
  Modal,
  useWindowDimensions,
  type LayoutChangeEvent,
} from "react-native";
import { useTranslation } from "../../../../i18n/useTranslation";
import { ComponentStyles } from "../../../../constants/componentStyles";
import {
  useSessionStore,
  getPersistedSendCashStepsMode,
} from "../../../../../../state/src/useSessionStore";
import type { SessionState, SendCashStepsMode } from "../../../../../../state/src/useSessionStore";
import { useApiWithLoading } from '../../../../hooks/useApiWithLoading';
import { useVisualViewportHeight } from '../../../../hooks/useVisualViewportHeight';
import { postFeeLookup } from "../../../../services/common.service";
import { ProductType } from "../../../../view-models/productType";
// import { useGlobalError } from '../../../uiComponents/GlobalErrorProvider';
import { useGlobalError }  from '../../../uiComponents/GlobalErrorProvider';

// Icons
import BackIcon from "../../../../../../../assets/images/BackIcon.svg";
import HelpIcon from "../../../../../../../assets/images/HelpIcon.svg";
import CloseIcon from "../../../../../../../assets/images/CloseIcon.svg";
import DownArrow from "../../../../../../../assets/images/DownArrowIcon.svg";
import AccountLockedIcon from "../../../../../../../assets/images/AccountLocked.svg";
import GroupId from "../../../../../../../assets/images/IconContainer.svg";
import WarninBlackIcon from "../../../../../../../assets/images/WarningBlackIcon.svg";
import { getFieldsForProductRequest, updateConsumerProfile, validateConsumer, postTransactionToKiosk, postAuthConsumer } from "../../../../services/send.service";
import {
  buildSendValidationRequestFromStore,
  extractErrorDetailFromValidateConsumerResponse,
  formatValidateConsumerErrorMessage,
} from "../../../../helper/sendValidationHelper";
import { buildTransactionParametersFromStore } from "../../../../helper/transactionParameterHelper";

// Send flow step components
// import EnterPhoneStep, { EnterPhoneStepHandle } from "./steps/EnterPhoneStep";
import EnterPhoneStep, { EnterPhoneStepHandle } from "../../common/steps/EnterPhoneStep";
import EnterSenderNameStep, { SenderFullNameStepHandle } from "./steps/EnterSenderNameStep";
// import SenderAddressStep, { SenderAddressStepHandle }  from "./steps/SenderAddressStep";
import SenderAddressStepNew, { SenderAddressStepHandle }  from "./steps/SenderAddressStepNew";
import SenderDateOfBirthStep, { SenderDateOfBirthStepHanle } from "./steps/SenderDateOfBirthStep";
import SenderIdentificationNumber, { SenderIdentificationStepHandle } from "./steps/SenderIdentificationNumber";
import ReceiverFullNameStep, { ReceiverFullNameStepHandle } from "./steps/ReceiverFullNameStep";
import PinInputStep from "./steps/PinInputStep";
import ConfirmPinStep from "./steps/ConfirmPinStep";
import OtpVerificationStep from "./steps/OtpVerificationStep";
import ChooseCountryStep from "./steps/ChooseCountryStep";
import ChooseStateStep from "./steps/ChooseStateStep";
import HowTheyReceiveStep from "./steps/HowTheyReceiveStep";
import SendingAmountEntryStep from "./steps/SendAmountEntryStep";
import ReviewAndEditStep, { ReviewAndEditStepHandle } from "./steps/ReviewAndEditStep";
import SenderIdentificationTypeStep from "./steps/SenderIdentificationTypeStep";
import ChooseStateOfIdStep from "./steps/ChooseStateOfIdStep";
import FraudPreventionStep from "../../common/steps/FraudPreventionStep";
import ReviewFinalStep from "./steps/ReviewFinalStep";
import ConfirmationStep from "../../common/steps/ConfirmationStep";
import HeadToCashierStep from "./steps/HeadToCashierStep";
import AdditionalInformationStep from "./steps/AdditionalInformationStep";
import { GffpHelper } from "../../../../helper/gffpHelper";
import { FieldsForProductInfo } from "../../../../view-models/FieldsForProductInfo";
import { logUpdateConsumerProfileDraft } from "../../../../helper/createProfileHelper";
import { SendMoneyPostUpdateConsumerProfileParameter } from "../../../../view-models/SendMoneyPostUpdateConsumerProfileParameter";
import { buildAuthConsumerPostParameter } from "../../../../helper/authConsumerHelper";
import { AuthConsumerInfo } from "../../../../view-models/AuthConsumerInfo";
import { RequiredFields } from "../../../../view-models/RequiredFields";
import  Colors  from "../../../../constants/colors";

import { Flag } from "../../../Flag";
import NeedHelpComponent from "../../common/NeedHelpComponent";
import CalenderContainer from "../../../../../../../assets/images/CalendarContainer-2.svg";
import GradientBackground from "../../../uiComponents/GradientBackground";
import { FeeLookupHelper } from "../../../../helper/feeLookupHelper";
import type { FeeInfo } from "../../../../view-models/feeInfo";
import { capturePosthogEvent, trackUIError } from '../../../../utils/analytics';
import { ProductFieldInfoVisibility } from "../../../../view-models/ProductFieldInfoVisibility";
import type { WebFlowRoutingProps } from "../../../../navigation/webFlowRouting";
import { useWebFlowStepSync } from "../../../../hooks/useWebFlowStepSync";

interface MainSendScreenProps {
  handleClose?: () => void;
  handleFinish?: () => void;
  /** Web: sync URL `/send/:stepSlug` with active step (optional; native omits). */
  webFlowRouting?: WebFlowRoutingProps;
}

/** identifications.json `key` for Mexican Electoral (spelling matches source data). */
const MEXICAN_ELECTORAL_ID_TYPE_KEY = "MexicanElectorialIdentification";
const PASSPORT_ID_TYPE_KEY = "Passport";

/** GFFP fields not collected in send UI (validation sends empty when product lists the tag). */
const GFFP_XML_TAGS_SKIPPED_SEND_UI = new Set(["customerreceivenumber"]);

function filterSendFlowMissingGffpFields(missing: any[] | undefined | null): any[] {
  if (!missing?.length) return missing || [];
  return missing.filter(
    (f) => !GFFP_XML_TAGS_SKIPPED_SEND_UI.has(String(f?.xmlTag ?? f?.XmlTag ?? "").toLowerCase())
  );
}

const BASE_STEPS_NEW = [
  { key: "phone", component: EnterPhoneStep },
  { key: "country", component: ChooseCountryStep },
  { key: "state", component: ChooseStateStep },
  { key: "sendingAmountEntry", component: SendingAmountEntryStep },
  { key: "howTheyReceive", component: HowTheyReceiveStep },
  { key: "reviewAndEditStep", component: ReviewAndEditStep },
  { key: "senderName", component: EnterSenderNameStep },
  { key: "senderAddress", component: SenderAddressStepNew },
  { key: "senderDOB", component: SenderDateOfBirthStep },
  { key: "senderIdentification", component: SenderIdentificationTypeStep },
  { key: "stateOfId", component: ChooseStateOfIdStep },
  { key: "senderIdNumber", component: SenderIdentificationNumber },
  { key: "receiverName", component: ReceiverFullNameStep },
  { key: "additionalInformation", component: AdditionalInformationStep },
  { key: "fraudPrevention", component: FraudPreventionStep },
  { key: "finalReview", component: ReviewFinalStep },
  { key: "pin", component: PinInputStep },
  { key: "confirmPin", component: ConfirmPinStep },
  { key: "pinCreated", component: ConfirmationStep },
  { key: "headToCashier", component: HeadToCashierStep },
];

const BASE_STEPS_EXISTING = [
  { key: "phone", component: EnterPhoneStep },
  { key: "pin", component: PinInputStep },
  { key: "country", component: ChooseCountryStep },
  { key: "state", component: ChooseStateStep },
  { key: "sendingAmountEntry", component: SendingAmountEntryStep },
  { key: "howTheyReceive", component: HowTheyReceiveStep },
  { key: "reviewAndEditStep", component: ReviewAndEditStep },
  { key: "senderName", component: EnterSenderNameStep },
  { key: "senderAddress", component: SenderAddressStepNew },
  { key: "senderDOB", component: SenderDateOfBirthStep },
  { key: "senderIdentification", component: SenderIdentificationTypeStep },
  { key: "stateOfId", component: ChooseStateOfIdStep },
  { key: "senderIdNumber", component: SenderIdentificationNumber },
  { key: "receiverName", component: ReceiverFullNameStep },
  { key: "additionalInformation", component: AdditionalInformationStep },
  { key: "fraudPrevention", component: FraudPreventionStep },
  { key: "finalReview", component: ReviewFinalStep },
  { key: "headToCashier", component: HeadToCashierStep },
];

const RESET_PIN_STEPS = [
  { key: "otp", component: OtpVerificationStep },
  { key: "pin", component: PinInputStep },
  { key: "confirmPin", component: ConfirmPinStep },
  { key: "pinCreated", component: ConfirmationStep },
];
 

export const MainSendCashScreen: React.FC<MainSendScreenProps> = ({
  handleClose,
  handleFinish,
  webFlowRouting,
}) => {
  const { t, i18n } = useTranslation();
  const { executeWithLoading } = useApiWithLoading();
  const { showError } = useGlobalError();
  const [currentStep, setCurrentStep] = useState(0);

  const [steps, setSteps] = useState(() => {
    const mode = getPersistedSendCashStepsMode();
    if (mode === "new") return BASE_STEPS_NEW;
    if (mode === "resetPin") return RESET_PIN_STEPS;
    return BASE_STEPS_EXISTING;
  });
  // const [currentStep, setCurrentStep] = useState(0);

  const sendCashStepsMode = useSessionStore(
    (s: SessionState) => (s as SessionState).sendCashStepsMode ?? "existing"
  );
  const setSendCashStepsMode = useSessionStore(
    (s: SessionState) => (s as SessionState).setSendCashStepsMode
  );

  /** Keep step component list aligned with persisted mode after reload (NEW vs EXISTING vs reset PIN). */
  useEffect(() => {
    const mode: SendCashStepsMode = sendCashStepsMode ?? "existing";
    if (mode === "new") setSteps(BASE_STEPS_NEW);
    else if (mode === "resetPin") setSteps(RESET_PIN_STEPS);
    else setSteps(BASE_STEPS_EXISTING);
  }, [sendCashStepsMode]);

  useWebFlowStepSync(webFlowRouting, steps, currentStep, setCurrentStep);

  const [returnToStep, setReturnToStep] = useState<string | null>(null);
  const [isStepValid, setIsStepValid] = useState(false);
  const [promoCode, setPromoCode] = useState("");
    const [errors, setErrors] = useState({
          promoCode: "",
      });
  const [reviewStepApiError, setReviewStepApiError] = useState<string | null>(null);
  const [finalReviewApiError, setFinalReviewApiError] = useState<string | null>(null);
  const [pinStepApiError, setPinStepApiError] = useState<string | null>(null);
  const [confirmPinApiError, setConfirmPinApiError] = useState<string | null>(null);
  const isSendCash = useSessionStore((s: SessionState) => (s as any).isSendCash);
  const isReceiver = useSessionStore((s: SessionState) => (s as any).isReceiver);
  const phoneStepRef = React.useRef<EnterPhoneStepHandle | null>(null);
  const SenderAddressStepRef = React.useRef<SenderAddressStepHandle | null>(null);
  const SenderFullNameStepRef = React.useRef<SenderFullNameStepHandle | null>(null);
  const SenderDOBStepRef = React.useRef<SenderDateOfBirthStepHanle | null>(null);
  const SenderIdentificationStepRef = React.useRef<SenderIdentificationStepHandle | null>(null);
  const ReceiverFullNameStepRef = React.useRef<ReceiverFullNameStepHandle | null>(null);
  const ReviewAndEditStepRef = React.useRef<ReviewAndEditStepHandle | null>(null);
  // Pin Verification
  // const [pin, setPin] = useState(["", "", "", ""]);
  const [attemptsLeft, setAttemptsLeft] = useState(3);
  const [locked, setLocked] = useState(false);
  const [showPinAttemptsWarning, setShowPinAttemptsWarning] = useState(false);
  const pinErrorResetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [showPopup, setShowPopup] = useState(false);
  const [needHelp, setNeedHelp] = useState(false);
  const [isDOBConfirm, setIsDOBCOnfirm] = useState(false);
  const [isReceiverFullnameConfirm, setIsReceiverFullnameConfirm] = useState(false);
  const [applyPromoCode, setApplyLoading] = useState(false);
  const [resetSignal, setResetSignal] = useState(0);
  const [isGradientExpand, setIsGradientExpand] = useState(false);
  const sendReviewGradientStepKeys = useMemo(
    () => new Set<string>(["reviewAndEditStep", "finalReview"]),
    []
  );
  /** Single-field send steps: open keyboard by default and keep Continue above the keyboard. */
  const sendStickyKeyboardContinueKeys = useMemo(
    () => new Set<string>(["phone", "senderDOB", "senderIdNumber"]),
    []
  );
  const sendCurrentStepKey = steps[currentStep]?.key;
  useEffect(() => {
    if (sendCurrentStepKey && sendReviewGradientStepKeys.has(sendCurrentStepKey)) {
      setIsGradientExpand(true);
    } else {
      setIsGradientExpand(false);
    }
  }, [sendCurrentStepKey, sendReviewGradientStepKeys]);

  const { height: screenHeight } = useWindowDimensions();
  /** Web (esp. iOS Safari): sync layout height with visual viewport when URL bar shows/hides. */
  const webVisualViewportHeight = useVisualViewportHeight();
  const layoutHeight =
    Platform.OS === "web" ? webVisualViewportHeight ?? screenHeight : screenHeight;
  const [headerHeight, setHeaderHeight] = useState(0);
  const [footerHeight, setFooterHeight] = useState(0);
  const inputs = useRef<(TextInput | null)[]>([]);
  /** When true, receiverName → fraudPrevention skipped additionalInformation (matches forward path). Used so Back from fraud does not show AI. */
  const skippedAdditionalInformationBeforeFraudRef = useRef(false);
  /** How we entered additionalInformation, so Back goes to the right step (not linear receiverName when from finalReview). */
  const additionalInformationNavSourceRef = useRef<"receiverReturnToFraud" | "finalReview" | null>(null);
  const setConsumerPin = useSessionStore((s: any) => s.setConsumerPin);

  const selectedDestinationCountryCode = useSessionStore((s: SessionState) => s.selectedDestinationCountryCode);
  const selectedCountryName = useSessionStore((s: SessionState) => s.selectedDestinationCountryName);
  const selectedDestinationStateAbbr = useSessionStore((s: SessionState) => s.selectedDestinationStateAbbr);
  const pendingSendOption = useSessionStore((s: SessionState) => (s as any).pendingSendOption);
  const selectedSendOptionValue = useSessionStore((s: SessionState) => (s as any).selectedSendOption);
  const setSelectedSendOption = useSessionStore((s: SessionState) => (s as any).setSelectedSendOption);
  const currentSendAmount = useSessionStore((s: SessionState) => (s as any).currentSendAmount);
  const theyReceiveAmount = useSessionStore((s: SessionState) => (s as any).theyReceiveAmount);
  const agentId = useSessionStore((s: SessionState) => s.agentId);
  const unitProfileId = useSessionStore((s: SessionState) => s.unitProfileId);
  const senderDOB = useSessionStore((s: SessionState) => s.senderDOB);
  const senderDOBStringFromStore = useSessionStore((s: any) => (s as any).senderDOBString);
  const selectedReceiveCurrency = useSessionStore((s: SessionState) => s.selectedReceiveCurrency);
  const setLastFeeLookupResponse = useSessionStore((s: SessionState) => (s as any).setLastFeeLookupResponse);
  const setSendFeeLookupReviewEditStore = useSessionStore((s: SessionState) => (s as any).setSendFeeLookupReviewEditStore);
  const setGffpCategories = useSessionStore((s: SessionState) => (s as any).setGffpCategories);
  const setGffpFieldsByCategory = useSessionStore((s: SessionState) => (s as any).setGffpFieldsByCategory);
  const setMissingRequiredFields = useSessionStore((s: SessionState) => (s as any).setMissingRequiredFields);
  const additionalFieldValues = useSessionStore((s: SessionState) => (s as any).additionalFieldValues);
  const selectedIdPhotoIdCountry = useSessionStore((s: SessionState) => (s as any).selectedIdPhotoIdCountry);
  const recordStepTransition = useSessionStore((s: SessionState) => (s as any).recordStepTransition);
  const isNewConsumer = useSessionStore((s: SessionState) => (s as any).isNewConsumer);
  // Sender profile setters to hydrate from AuthConsumer
  const setConsumerId = useSessionStore((s: SessionState) => (s as any).setConsumerId);
  const setSenderFirstName = useSessionStore((s: SessionState) => (s as any).setSenderFirstName);
  const setSenderMiddleName = useSessionStore((s: SessionState) => (s as any).setSenderMiddleName);
  const setSenderLastName = useSessionStore((s: SessionState) => (s as any).setSenderLastName);
  const setSenderAddress = useSessionStore((s: SessionState) => (s as any).setSenderAddress);
  const setSenderCity = useSessionStore((s: SessionState) => (s as any).setSenderCity);
  const setSenderState = useSessionStore((s: SessionState) => (s as any).setSenderState);
  const setSenderZipCode = useSessionStore((s: SessionState) => (s as any).setSenderZipCode);
  const setSenderDOBString = useSessionStore((s: SessionState) => (s as any).setSenderDOBString);
  const setSenderDOB = useSessionStore((s: SessionState) => (s as any).setSenderDOB);
  const setPhoneNumber = useSessionStore((s: SessionState) => (s as any).setPhoneNumber);
  const setConfirmationNumber = useSessionStore((s: SessionState) => (s as any).setConfirmationNumber);
  const setFinalSendValidationResponseStore = useSessionStore((s: SessionState) => (s as any).setFinalSendValidationResponseStore);
  const consumerIdFromStore = useSessionStore((s: SessionState) => (s as any).consumerId);
  const StepComponent = steps[currentStep].component;
  const AnyStepComponent = StepComponent as any;
  const setIsNewConsumer = useSessionStore((s: SessionState) => (s as any).setIsNewConsumer);
  const setIsPromocodeApplied = useSessionStore((s: SessionState) => (s as any).setIsPromocodeApplied);
  const setIsPromoActivityIndicator = useSessionStore((s: SessionState) => (s as any).setIsPromoActivityIndicator);
  const receiverFirstName = useSessionStore((s: any) => s.receiverFirstName);
  const receiverLastName = useSessionStore((s: any) => s.receiverLastName);
  const setSelectedSendOptionStore = useSessionStore((s: SessionState) => (s as any).setSelectedSendOption);
  const setPendingSendOptionStore = useSessionStore((s: SessionState) => (s as any).setPendingSendOption);
  const lastFeeLookupResponse = useSessionStore((s: SessionState) => (s as any).lastFeeLookupResponse);
  const selectedDeliveryOptionStore = useSessionStore((s: SessionState) => (s as any).selectedDeliveryOptionStore);
  const selectedSendOption = useSessionStore((s: SessionState) => (s as any).selectedSendOption);
  const [preApplyFees, setPreApplyFees] = useState<number | null>(null);
  const [postApplyFees, setPostApplyFees] = useState<number | null>(null);
  const feeLookupParamsFromStore = useSessionStore((s: SessionState) => (s as any).sendFeeLookupReviewEditStore);
  const isPromocodeApplied = useSessionStore((s: SessionState) => (s as any).isPromocodeApplied);
  // capturing below details in posthog events
  const senderFirstName = useSessionStore((s: SessionState) => (s as any).senderFirstName);
  const senderMiddleName = useSessionStore((s: SessionState) => (s as any).senderMiddleName);
  const senderLastName = useSessionStore((s: SessionState) => (s as any).senderLastName);
  const senderAddress = useSessionStore((s: SessionState) => (s as any).senderAddress);
  const senderCity = useSessionStore((s: SessionState) => (s as any).senderCity);
  const senderZipCode = useSessionStore((s: SessionState) => (s as any).senderZipCode);
  const senderState = useSessionStore((s: SessionState) => (s as any).senderState);
  const storedDobString = useSessionStore((s: SessionState) => (s as any).senderDOBString);
  const selectedIdTypeKey = useSessionStore((s: SessionState) => (s as any).selectedIdTypeKey);
  const selectedIdTypeValue = useSessionStore((s: SessionState) => (s as any).selectedIdTypeValue);
  const selectedIdTypeLabel = useSessionStore((s: SessionState) => (s as any).selectedIdTypeLabel);
  const selectedIdPhotoIdStateAbbr = useSessionStore((s: SessionState) => (s as any).selectedIdPhotoIdStateAbbr);
  const selectedIdPhotoIdStateName = useSessionStore((s: SessionState) => (s as any).selectedIdPhotoIdStateName);
  const senderPhotoIdNumber = useSessionStore((s: SessionState) => (s as any).senderPhotoIdNumber);
  const storeFirst = useSessionStore((s: any) => s.receiverFirstName);
  const storeMiddle = useSessionStore((s: any) => s.receiverMiddleName);
  const storeLast = useSessionStore((s: any) => s.receiverLastName);
  const storeLast2 = useSessionStore((s: any) => s.receiverLastName2);
  const consumerPin = useSessionStore((s: any) => s.consumerPin);

  const option: any = pendingSendOption || selectedSendOption || null;

  const selectedDeliveryFeeInfo: FeeInfo | undefined = useMemo(() => {
      const arr: FeeInfo[] = Array.isArray((lastFeeLookupResponse as any)?.FeeInfos)
        ? (lastFeeLookupResponse as any).FeeInfos
        : (Array.isArray((lastFeeLookupResponse as any)?.FeeInfo) ? (lastFeeLookupResponse as any).FeeInfo : []);
      const sel = ((selectedDeliveryOptionStore as any)?.DeliveryOption ?? '').toString().toUpperCase();
      if (!arr || arr.length === 0) return undefined;
      if (!sel) return arr[0];
      return arr.find((f: any) => {
        const opt = (f?.DeliveryOption ?? f?.DeliveryOptId ?? '').toString().toUpperCase();
        return !!opt && opt === sel;
      }) || arr[0];
    }, [lastFeeLookupResponse, selectedDeliveryOptionStore]);
  
  const summary = useMemo(() => {
      if (!option) {
        return {
          sendAmount: 0,
          sendCurrency: 'USD',
          receiveAmount: 0,
          receiveCurrency: '—',
          exchangeRate: 0,
          fees: 0,
          total: 0,
        };
      }
      const sendCurrency: string = option?.SendAmounts?.SendCurrency || 'USD';
      const sendAmount: number = Number(option?.SendAmounts?.SendAmount || 0);
      const receiveCurrency: string = option?.ReceiveAmounts?.ValidCurrencyIndicator
        ? (option?.ValidReceiveCurrency || '')
        : (option?.EstimatedReceiveCurrency || option?.ValidReceiveCurrency || '');
      const receiveAmount: number = Number(option?.ValidReceiveAmount ?? option?.EstimatedReceiveAmount ?? 0);
      const exchangeRateNum: number = Number(FeeLookupHelper.getExchangeRate(option) || 0);
      const fees: number = Number(option?.SendAmounts?.TotalDiscountAmount || 0) + Number(option?.SendAmounts?.TotalSendFees || 0);
      const totalBase: number = Number(option?.TotalAmount || 0);
      const total: number = totalBase;
      return { sendAmount, sendCurrency, receiveAmount, receiveCurrency, exchangeRate: exchangeRateNum, fees, total };
    }, [pendingSendOption, selectedSendOption]);

    const scrollRef = useRef<ScrollView>(null);

    useEffect(() => {
      // scroll to top on step change
      requestAnimationFrame(() => {
        scrollRef.current?.scrollTo({ y: 0, animated: false });
      });
    }, [currentStep]);

    // Dev-only: open Additional Information step with mock data via ?testAdditionalInfo=1 (web)
    // useEffect(() => {
    //   if (typeof window === "undefined" || !window.location?.search) return;
    //   const params = new URLSearchParams(window.location.search);
    //   if (params.get("testAdditionalInfo") !== "1") return;
    //   const mockCategory = "Destination Information";
    //   const mockOccupation = {
    //     xmlTag: "receiverOccupation",
    //     XmlTag: "receiverOccupation",
    //     fieldLabel: "Their current occupation",
    //     FieldLabel: "Their current occupation",
    //     fieldCategory: mockCategory,
    //     FieldCategory: mockCategory,
    //     visibility: ProductFieldInfoVisibility.REQ,
    //     Visibility: ProductFieldInfoVisibility.REQ,
    //     enumeratedValues: [
    //       { Value: "ACC", Label: "Accountant" },
    //       { Value: "AER", Label: "Aerospace" },
    //       { Value: "AGR", Label: "Agriculture" },
    //       { Value: "ART", Label: "Artist" },
    //       { Value: "BUS", Label: "Business" },
    //       { Value: "COM", Label: "Communications" },
    //       { Value: "CON", Label: "Construction" },
    //       { Value: "CNS", Label: "Consulting" },
    //       { Value: "DIG", Label: "Digital Services" },
    //     ],
    //     EnumeratedValues: [
    //       { Value: "ACC", Label: "Accountant" },
    //       { Value: "AER", Label: "Aerospace" },
    //       { Value: "AGR", Label: "Agriculture" },
    //       { Value: "ART", Label: "Artist" },
    //       { Value: "BUS", Label: "Business" },
    //       { Value: "COM", Label: "Communications" },
    //       { Value: "CON", Label: "Construction" },
    //       { Value: "CNS", Label: "Consulting" },
    //       { Value: "DIG", Label: "Digital Services" },
    //     ],
    //   };
    //   const mockCountryOfBirth = {
    //     xmlTag: "receiverCountryOfBirth",
    //     XmlTag: "receiverCountryOfBirth",
    //     fieldLabel: "Country of Birth",
    //     FieldLabel: "Country of Birth",
    //     fieldCategory: mockCategory,
    //     FieldCategory: mockCategory,
    //     visibility: ProductFieldInfoVisibility.REQ,
    //     Visibility: ProductFieldInfoVisibility.REQ,
    //     enumeratedValues: [
    //       { Value: "USA", Label: "United States" },
    //       { Value: "MEX", Label: "Mexico" },
    //       { Value: "IND", Label: "India" },
    //       { Value: "PHL", Label: "Philippines" },
    //       { Value: "GBR", Label: "United Kingdom" },
    //     ],
    //     EnumeratedValues: [
    //       { Value: "USA", Label: "United States" },
    //       { Value: "MEX", Label: "Mexico" },
    //       { Value: "IND", Label: "India" },
    //       { Value: "PHL", Label: "Philippines" },
    //       { Value: "GBR", Label: "United Kingdom" },
    //     ],
    //   };
    //   const mockFields = [mockOccupation, mockCountryOfBirth];
    //   const mockGrouped: Record<string, any[]> = { [mockCategory]: mockFields };
    //   try {
    //     setGffpFieldsByCategory?.(mockGrouped);
    //     setMissingRequiredFields?.(mockFields as any);
    //     setSteps([{ key: "additionalInformation", component: AdditionalInformationStep }]);
    //     setCurrentStep(0);
    //   } catch (e) {
    //     console.warn("testAdditionalInfo: failed to inject mock data", e);
    //   }
    // }, []);


    const updatePromocode = (promo: string) => {
      setPromoCode(promo);
      setErrors(prev => ({ ...prev, promoCode: "" }));

    };

    const clearPromoCode = () => {
      setPromoCode("");
      setIsPromocodeApplied(false)
    }

    const handleApplyPromoCode = async() => {
      // Validation
      if (!promoCode.trim()) {
        setErrors(prev => ({ ...prev, promoCode: "Please enter a promo code" }));
        return;
      }
      setIsPromoActivityIndicator(true);
      setPreApplyFees(summary.fees);
      try {
        const updatedParams = {
          ...(feeLookupParamsFromStore || {}),
          promoCodes: promoCode ? [promoCode] : undefined,
          deliveryOption: ((selectedDeliveryOptionStore as any)?.DeliveryOption ?? undefined),
          allOptions: false,
        } as any;

        try { setSendFeeLookupReviewEditStore?.(updatedParams); } catch {}
  
        const feeResp = await postFeeLookup(updatedParams as any);
  
        // Normalize response
        const rawData: any = (feeResp && (feeResp as any).data !== undefined) ? (feeResp as any).data : feeResp;
        let parsed: any = rawData;
        if (typeof rawData === 'string') {
          try { parsed = JSON.parse(rawData); } catch {}
        }
  
        // Inspect potential error envelope in fee lookup response
        const errDetail = parsed?.ErrorDetail || parsed?.errorDetail;
        if (errDetail) {
          const errCode = errDetail?.ErrorCode ?? errDetail?.errorCode ?? 'UNKNOWN';
          const errString = errDetail?.ErrorString ?? errDetail?.errorString ?? 'An error occurred.';
          console.warn('FeeLookup ErrorDetail:', { errCode, errString });
          //showError(errCode + ' ' + errString);
          
          trackUIError(errCode + ' ' + errString, 'Promo Code Error');
          showError(t('PROMOCODE_ERROR_OCCURRED'));
          return;
        }
  
        try { setLastFeeLookupResponse?.(parsed); } catch {}
  
        const feeInfos: any[] = Array.isArray(parsed?.FeeInfos)
          ? parsed?.FeeInfos
          : (Array.isArray(parsed?.FeeInfo) ? parsed?.FeeInfo : []);
  
        // Try to find the updated option matching current selection
        const current = option || {};
        const match = feeInfos.find((f: any) => {
          const sameDisplay = (f?.DeliveryOptDisplayName || '') === (current?.DeliveryOptDisplayName || '');
          const sameAgent = (f?.ReceiveAgentId || f?.ReceiveAgentID) === (current?.ReceiveAgentId || current?.ReceiveAgentID);
          return sameDisplay || sameAgent;
        }) || feeInfos[0];
  
        if (!match) {
          return;
        }
  
        // Check promotion status
        const promoArray: any[] = Array.isArray(match?.PromotionInfo) ? match?.PromotionInfo : [];
        const hasError = promoArray.some((p: any) => !!(p?.PromotionErrorCode || p?.promotionErrorCode));
        if (!hasError) {
          // Apply discount to fees if necessary (prefer server-calculated values)
          const discountAmt = Number(match?.SendAmounts?.TotalDiscountAmount ?? 0)
            || Number(promoArray?.reduce((sum: number, p: any) => sum + Number(p?.PromotionDiscountAmount || 0), 0));
          const originalFees = Number(match?.SendAmounts?.TotalSendFees ?? 0);
          const taxes = Number(match?.SendAmounts?.TotalSendTaxes ?? 0);
          const sendAmt = Number(match?.SendAmounts?.SendAmount ?? 0);
          const discountedFees = Math.max(0, originalFees - (discountAmt || 0));
  
          const totalAmountToCollect = (typeof match?.SendAmounts?.TotalAmountToCollect === 'number')
            ? Number(match?.SendAmounts?.TotalAmountToCollect)
            : (sendAmt + discountedFees + taxes);
  
          const updatedOption = {
            ...match,
            SendAmounts: {
              ...(match?.SendAmounts || {}),
              TotalDiscountAmount: discountAmt || match?.SendAmounts?.TotalDiscountAmount,
              TotalSendFees: discountedFees,
              TotalAmountToCollect: totalAmountToCollect,
            },
            TotalAmount: typeof match?.TotalAmount === 'number' ? match?.TotalAmount : totalAmountToCollect,
          } as any;
  
          // Capture new fees (discount + discountedFees) for display
          try { setPostApplyFees((discountAmt || 0) + (discountedFees || 0)); } catch {}
          try { setSelectedSendOptionStore?.(updatedOption); } catch {}
          try { setPendingSendOptionStore?.(updatedOption); } catch {}
        }
        setIsPromoActivityIndicator(false);
        setIsPromocodeApplied(true);
      } catch (e: any) {
        setErrors(prev => ({ ...prev, promoCode: "Something went wrong" }));
        setIsPromoActivityIndicator(false);
        setIsPromocodeApplied(true);
        console.warn('handleApplyPromoCode error:', e);
        try { showError(e?.message || 'An error occurred applying the promo code.'); } catch {}
      } finally {
        setIsPromoActivityIndicator(false);
        setIsPromocodeApplied(true);
      }
    }

  // initialize timing for first step
  useEffect(() => {
    try { recordStepTransition?.(steps[0].key); } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Callback each step can use to report validity
  const handleValidationChange = (valid: boolean) => {
    setIsStepValid(valid);
  };

  const handleEditStep = (key: string) => {
     if (key === 'country') {
      // ✅ Go to country step, but DO NOT set returnToStep.
      const targetIndex = steps.findIndex(s => s.key === 'country');
      if (targetIndex !== -1) setCurrentStep(targetIndex);
    } else {
      const targetIndex = steps.findIndex(s => s.key === key);
      if (targetIndex !== -1) {
        setReturnToStep(steps[currentStep].key);   // "reviewAndEditStep"✅ remember where we came from
        setCurrentStep(targetIndex); // 7th index
      }}
  };

  const handlePinVerification = async () => {
    // replace with real validation
    const invalidPinMessage = "Incorrect PIN. Please try again.";
    const handlePinVerificationFailure = () => {
      if (pinErrorResetTimeoutRef.current) {
        clearTimeout(pinErrorResetTimeoutRef.current);
        pinErrorResetTimeoutRef.current = null;
      }
      setPinStepApiError(invalidPinMessage);
      setShowPinAttemptsWarning(true);
      const remaining = attemptsLeft - 1;
      setAttemptsLeft(remaining);
      if (remaining <= 0) {
        setLocked(true);
        setShowPopup(true);
        setResetSignal(prev => prev + 1);
        setConsumerPin("");
        inputs.current[0]?.focus();
        return;
      }
      pinErrorResetTimeoutRef.current = setTimeout(() => {
        setPinStepApiError(null);
        setResetSignal(prev => prev + 1);
        setConsumerPin("");
        inputs.current[0]?.focus();
        pinErrorResetTimeoutRef.current = null;
      }, 350);
    };

    try {
          const authConsumerPostParameter = buildAuthConsumerPostParameter();
          const authConsumerResponse: AuthConsumerInfo = await postAuthConsumer(authConsumerPostParameter);
          const respAny: any = authConsumerResponse as any;
          if (respAny?.ErrorDetail || respAny?.errorDetail) {
            handlePinVerificationFailure();
            return; // stay on PIN step when auth returns an error detail
          }
          // Hydrate sender profile details into the store from successful AuthConsumer
          const ci: any = respAny?.ConsumerInfo ?? respAny?.consumerInfo;
          if (ci) {
            // Consumer ID
            try { setConsumerId?.(String(ci.ConsumerId ?? ci.consumerId ?? '')); } catch {}
            try { setSenderFirstName?.(ci.SenderFirstName ?? ci.senderFirstName ?? null); } catch {}
            try { setSenderMiddleName?.(ci.SenderMiddleName ?? ci.senderMiddleName ?? null); } catch {}
            try { setSenderLastName?.(ci.SenderLastName ?? ci.senderLastName ?? null); } catch {}
            try { setSenderAddress?.(ci.SenderAddress ?? ci.senderAddress ?? null); } catch {}
            try { setSenderCity?.(ci.SenderCity ?? ci.senderCity ?? null); } catch {}
            try { setSenderState?.(ci.SenderState ?? ci.senderState ?? null); } catch {}
            try { setSenderZipCode?.(ci.SenderZipCode ?? ci.senderZipCode ?? null); } catch {}
            // Phone
            try { setPhoneNumber?.(ci.SenderHomePhone ?? ci.senderHomePhone ?? null); } catch {}
            // DOB
            const dobRaw: any = ci.SenderDOB ?? ci.senderDOB ?? null;
            if (dobRaw) {
              const dobStr = String(dobRaw).split('T')[0]; // handle YYYY-MM-DD or YYYY-MM-DDThh:mm:ss
              try { setSenderDOBString?.(dobStr || null); } catch {}
              // Parse YYYY-MM-DD at noon local time to avoid timezone shifts
              // This prevents the date from showing one day less in the confirmation modal
              if (/^\d{4}-\d{2}-\d{2}/.test(dobStr)) {
                const y = Number(dobStr.slice(0, 4));
                const m = Number(dobStr.slice(5, 7)) - 1;
                const d = Number(dobStr.slice(8, 10));
                const dobDate = new Date(y, m, d, 12, 0, 0, 0);
                if (!isNaN(dobDate as any)) {
                  try { setSenderDOB?.(dobDate); } catch {}
                }
              } else {
                // Fallback for other formats
                const dobDate = new Date(dobStr);
                if (!isNaN(dobDate as any)) {
                  try { setSenderDOB?.(dobDate); } catch {}
                }
              }
            }
          }
        } catch (e) {
          console.warn("PIN verification failed", e);
          handlePinVerificationFailure();
          return;
        }
        if (pinErrorResetTimeoutRef.current) {
          clearTimeout(pinErrorResetTimeoutRef.current);
          pinErrorResetTimeoutRef.current = null;
        }
        setShowPinAttemptsWarning(false);
        const targetIndex = steps.findIndex(s => s.key === "country");
        if (targetIndex !== -1) {
          setCurrentStep(targetIndex);
          return;
        }
  };

  useEffect(() => {
    return () => {
      if (pinErrorResetTimeoutRef.current) {
        clearTimeout(pinErrorResetTimeoutRef.current);
      }
    };
  }, []);

  const handleReceiverInformation = () => {
        try {
          const gffpFieldsByCategoryLocal = useSessionStore.getState().gffpFieldsByCategory || {};
          const consumerId = consumerIdFromStore ? String(consumerIdFromStore) : "0";
          const validationReq = buildSendValidationRequestFromStore(consumerId);
          const missing = filterSendFlowMissingGffpFields(
            new RequiredFields().getMissingFields(gffpFieldsByCategoryLocal, validationReq)
          );
          if (missing && missing.length > 0) {
            try { setMissingRequiredFields?.(missing as any); } catch {}
            skippedAdditionalInformationBeforeFraudRef.current = false;
            additionalInformationNavSourceRef.current = "receiverReturnToFraud";
            // Route to Additional Information, then return back to Fraud Prevention after completion
            setReturnToStep("fraudPrevention");
            const aiIndex = steps.findIndex(s => s.key === "additionalInformation");
            if (aiIndex !== -1) {
              setCurrentStep(aiIndex);
              return;
            }
          }
        } catch (e) {
          console.warn('Failed to compute missing fields after receiverName', e);
        }
        captureReceiverNameConfirmationEvent(storeFirst, storeMiddle, storeLast, storeLast2);
        // No missing fields → proceed to Fraud Prevention
        skippedAdditionalInformationBeforeFraudRef.current = true;
        const targetIndex = steps.findIndex(s => s.key === "fraudPrevention");
        if (targetIndex !== -1) {
          try { recordStepTransition?.("fraudPrevention"); } catch {}
          setCurrentStep(targetIndex);
          return;
        }
  }

  const captureAmountEvent = (currentSendAmount: string, theyReceiveAmount: any) => {
    capturePosthogEvent('Send Flow - Amount Capture Step', {
      'data-mg-sending-amount': currentSendAmount,
      'data-mg-receiving-amount': theyReceiveAmount
    });
  }

  const captureInitialReviewWithPromoCodeEvent = (promocodeValue: string, isPromocodeApplied: boolean) => {
    capturePosthogEvent('Send Flow - Initial Review with Promo Code Step', {
      'data-mg-promocode': isPromocodeApplied ? promocodeValue : 'N/A'
    });
  }

  const captureSenderFullnameEvent = (senderFirstName: string, senderMiddleName: string, senderLastName: string) => {
    capturePosthogEvent('Send Flow - Sender Name Capture Step', {
      'data-mg-sender-firstName': senderFirstName,
      'data-mg-sender-middleName': senderMiddleName,
      'data-mg-sender-lastName': senderLastName,
    });
  }

  const captureSenderAddressEvent = (senderAddress: string, senderCity: string, senderZipCode: string, senderState: string) => {
    capturePosthogEvent('Send Flow - Sender Address Capture Step', {
      'data-mg-sender-address': senderAddress,
      'data-mg-sender-city': senderCity,
      'data-mg-sender-zipcode': senderZipCode,
      'data-mg-sender-state': senderState,
    });
  }

  const captureSenderDOBEvent = (senderDOB: string) => {
    capturePosthogEvent('Send Flow - Sender DOB Capture Step', {
      'data-mg-sender-dob': senderDOB
    });
  }

  const captureSenderDOBConfirmationEvent = (senderDOB: string) => {
    capturePosthogEvent('Send Flow - Sender DOB Confirmation Step', {
      'data-mg-sender-dob': senderDOB
    });
  }

  const captureSenderIdType = (selectedIdTypeKey: string, selectedIdTypeValue: string, selectedIdTypeLabel: string, selectedIdPhotoIdCountry: string, selectedIdPhotoIdStateAbbr: string, selectedIdPhotoIdStateName: string) => {
    capturePosthogEvent('Send Flow - Sender ID Type Capture Step', {
      'data-mg-sender-id-type-key': selectedIdTypeKey,
      'data-mg-sender-id-type-value': selectedIdTypeValue,
      'data-mg-sender-id-type-label': selectedIdTypeLabel,
      'data-mg-sender-photoId-country': selectedIdPhotoIdCountry,
      'data-mg-sender-photoId-state-abbr': selectedIdPhotoIdStateAbbr,
      'data-mg-sender-photoId-state-name': selectedIdPhotoIdStateName,
    });
  }
  const captureSenderIdentificationNumbervent = (senderPhotoIdNumber: string) => {
    capturePosthogEvent('Send Flow - Sender ID Number Capture Step', {
      'data-mg-sender-identification-number': senderPhotoIdNumber
    });
  }

  const captureReceiverNameEvent = (storeFirst: string, storeMiddle: string, storeLast: string, storeLast2: string) => {
    capturePosthogEvent('Send Flow - Receiver Name Capture Step', {
      'data-mg-receiver-firstname': storeFirst,
      'data-mg-receiver-middlename': storeMiddle,
      'data-mg-receiver-lastname': storeLast,
      'data-mg-receiver-lastname2': storeLast2,
    });
  }

  const captureReceiverNameConfirmationEvent = (storeFirst: string, storeMiddle: string, storeLast: string, storeLast2: string) => {
    capturePosthogEvent('Send Flow - Receiver Name Confirmation Step', {
      'data-mg-receiver-firstname': storeFirst,
      'data-mg-receiver-middlename': storeMiddle,
      'data-mg-receiver-lastname': storeLast,
      'data-mg-receiver-lastname2': storeLast2,
    });
  }

  const captureFraudPreventionEvent = () => {
    capturePosthogEvent('Send Flow - Fraud Prevention Step', {
      'data-mg-fraud-prevention': 'true'
    });
  }

  const captureFinalReviewEvent = () => {
    capturePosthogEvent('Send Flow - Final Review Step', {
      'data-mg-final-review': 'true'
    });
  }

  const captureConsumerPinEvent = (consumerPin: string) => {
    capturePosthogEvent('Send Flow - Consumer PIN Capture Step', {
      'data-mg-consumer-pin': consumerPin
    });
  }

  const captureConfirmPinEvent = (consumerPin: string) => {
    capturePosthogEvent('Send Flow - Confirm PIN Step', {
      'data-mg-consumer-pin': consumerPin
    });
  }
  
  const capturePinCreatedEvent = () => {
    capturePosthogEvent('Send Flow - PIN Created Step', {
      'data-mg-pin-created': 'true'
    });
  }

  const handleNext = async (countryCode?: string) => {
    // Only EnterPhoneStep triggers the ConsumerLookup
    await executeWithLoading(async () => {
      // remember returnToStep when edit happening
      if (
        returnToStep &&
        steps[currentStep].key !== "sendingAmountEntry" &&
        steps[currentStep].key !== "howTheyReceive"
      ) {
        const targetIndex = steps.findIndex(s => s.key === returnToStep);
        if (targetIndex !== -1) {
          setCurrentStep(targetIndex);
        }
        setReturnToStep(null);   // clear for next time
        return;                  // stop normal forward flow
      }
      // ---- normal forward navigation below ----
      if (steps[currentStep].key === "phone") {
        try {
          const isValid = await phoneStepRef.current?.phoneSubmit();
          if (!isValid) return;
          const result = await phoneStepRef.current?.submit();
          if (result === 'notfound' || result === 'externalSenderProfile') {
            // Treat as new consumer (or external profile without local PIN — same flow)
            handleCustomerNotFound();
            setSteps(() => {
              return BASE_STEPS_NEW;
            });
            setSendCashStepsMode?.("new");
            // advance on the next render so BASE_STEPS_NEW is fixed
            requestAnimationFrame(() => setCurrentStep(
                BASE_STEPS_NEW.findIndex(s => s.key === 'country')
            ));
            return;
          } else {
            // Existing consumer
            setSteps(BASE_STEPS_EXISTING);
            setSendCashStepsMode?.("existing");
            setCurrentStep(1); // move to PinInput
          }
          // Success path: go to PIN step
          const targetIndex = steps.findIndex(s => s.key === "pin");
          if (targetIndex !== -1) {
            setCurrentStep(targetIndex);
            return;
          }
        } catch {
          // submit already surfaced alerts; just stop progression
          return;
        }
      } else if (steps[currentStep].key === "country") {
        // After selecting a country, branch to state or amount entry
        const isUsOrCa = countryCode === 'USA' || countryCode === 'CAN';
        const targetKey = isUsOrCa ? 'state' : 'sendingAmountEntry';
        const targetIndex = steps.findIndex(s => s.key === targetKey);
        if (targetIndex !== -1) {
          setCurrentStep(targetIndex);
          return;
        }
      } else if (steps[currentStep].key === "state") {
        // After selecting a country, branch to state or amount entry
        const targetIndex = steps.findIndex(s => s.key === 'sendingAmountEntry');
        if (targetIndex !== -1) {
          setCurrentStep(targetIndex);
          return;
        }
      } else if (steps[currentStep].key === "otp") {
        // OTP verification step - after OTP is verified, proceed to enter new PIN
        // TODO: Add actual OTP verification API call here
        // For now, just proceed to PIN step
        const pinIndex = steps.findIndex(s => s.key === "pin");
        if (pinIndex !== -1) {
          try { recordStepTransition?.("pin"); } catch {}
          setCurrentStep(pinIndex);
          return;
        }
      } else if (steps[currentStep].key === "pin") {
        // Check if we're in reset PIN flow (steps array contains "otp" key)
        const isResetPinFlow = steps.some(s => s.key === "otp");
        
        if (isResetPinFlow) {
          // In reset PIN flow: after entering new PIN, go to confirm PIN
          captureConsumerPinEvent(consumerPin);
          const confirmIndex = steps.findIndex(s => s.key === "confirmPin");
          if (confirmIndex !== -1) {
            try { recordStepTransition?.("confirmPin"); } catch {}
            setCurrentStep(confirmIndex);
            return;
          }
        } else if (isNewConsumer) {
          // If this is a new consumer, go to confirm PIN step instead of auth
          captureConsumerPinEvent(consumerPin)
          const confirmIndex = steps.findIndex(s => s.key === "confirmPin");
          if (confirmIndex !== -1) {
            try { recordStepTransition?.("confirmPin"); } catch {}
            setCurrentStep(confirmIndex);
            return;
          }
        } else {
          // Existing consumer: build the auth consumer request and proceed to choose country
          handlePinVerification();
        }
         
      } else if (steps[currentStep].key === "confirmPin") {
        // Check if we're in reset PIN flow
        const isResetPinFlow = steps.some(s => s.key === "otp");
        
        if (isResetPinFlow) {
          // In reset PIN flow: after confirming PIN, go to PIN created
          captureConfirmPinEvent(consumerPin);
          const pinCreatedIndex = steps.findIndex(s => s.key === "pinCreated");
          if (pinCreatedIndex !== -1) {
            try { recordStepTransition?.("pinCreated"); } catch {}
            setCurrentStep(pinCreatedIndex);
            return;
          }
        } else {
          // Normal flow
          captureConfirmPinEvent(consumerPin);
          // After confirming PIN for a new consumer, go to PIN created or head to cashier path
          // No early redirect to additionalInformation here; we will handle missing fields after receiverName
          const result = await maultipleCalls();
          if (result && (result as any).error) {
            setConfirmPinApiError((result as any).errorMessage ?? 'An error occurred.');
            return;
          }
          if(isNewConsumer) {
            const targetIndex = steps.findIndex(s => s.key === "pinCreated");
            if (targetIndex !== -1) {
              try { recordStepTransition?.("pinCreated"); } catch {}
              setCurrentStep(targetIndex);
              return;
            }
          }
          
          const targetIndex = steps.findIndex(s => s.key === "headToCashier");
          if (targetIndex !== -1) {
            try { recordStepTransition?.("headToCashier"); } catch {}
            setCurrentStep(targetIndex);
            return;
          }
        }
      } else if (steps[currentStep].key === "sendingAmountEntry") {
        if (!isStepValid){
          showError('Please enter valid amount');
          return
        }
        // Call FeeLookup with updated amount before moving to HowTheyReceive
        if (agentId && unitProfileId && selectedDestinationCountryCode && currentSendAmount) {
          const amountNum = Number(String(currentSendAmount).replace(/,/g, ""));
          if (isFinite(amountNum) && amountNum > 0) {
            const feeLookupParams = {
              agentID: agentId,
              unitProfileID: unitProfileId,
              amount: amountNum,
              amountExcludingFee: amountNum,
              sendCurrency: (pendingSendOption?.SendAmounts?.SendCurrency) || 'USD',
              destinationCountry: selectedDestinationCountryCode,
              destinationState: selectedDestinationStateAbbr,
              receiveCurrency: selectedReceiveCurrency || undefined,
              allOptions: true,
              productType: ProductType.SEND,
            } as const;
            try { setSendFeeLookupReviewEditStore?.(feeLookupParams as any); } catch {}
            const feeResp = await postFeeLookup(feeLookupParams as any);
            let parsed: any;
            // Inspect potential error envelope in fee lookup response
            try {
              const rawData: any = (feeResp && (feeResp as any).data !== undefined) ? (feeResp as any).data : feeResp;
              parsed = rawData;
              if (typeof rawData === 'string') {
                try { parsed = JSON.parse(rawData); } catch {}
              }
              const errDetail = parsed?.ErrorDetail || parsed?.errorDetail;
              if (errDetail) {
                const errCode = errDetail?.ErrorCode ?? errDetail?.errorCode ?? 'UNKNOWN';
                const errString = errDetail?.ErrorString ?? errDetail?.errorString ?? 'An error occurred.';
                console.warn('FeeLookup ErrorDetail:', { errCode, errString });
               // Alert.alert('Message', String(errString), [{ text: 'OK' }]);
                showError(errCode + ' ' + errString);
                return; // stay on current step if fee lookup returned an error
              }
            } catch {}
            try {
             setLastFeeLookupResponse?.(parsed);
            } catch { }
          }
        }
        // commenting the below line, after making amount change we need to navigate to howtheyrecieve screen
        // If we came from review via Edit, return back to that step after fee lookup
        // if (returnToStep) {
        //   const targetIndex = steps.findIndex(s => s.key === returnToStep);
        //   if (targetIndex !== -1) {
        //     setCurrentStep(targetIndex);
        //   }
        //   setReturnToStep(null);
        //   return;
        // }
        
        captureAmountEvent(currentSendAmount, theyReceiveAmount);
        const targetIndex = steps.findIndex(s => s.key === "howTheyReceive");
        if (targetIndex !== -1) {
          setCurrentStep(targetIndex);
          return;
        }
      }
      /*else if (currentStep < steps.length - 1) {
        setCurrentStep((prev) => prev + 1);
        return;
      }*/
      else if (steps[currentStep].key === "howTheyReceive") {
        // Always read latest pending option from the store. Auto-advance runs in setTimeout(0) while
        // `isStepValid` in this closure can still be stale; the child sets pending synchronously.
        const pendingFromStore = (useSessionStore.getState() as any).pendingSendOption;
        if (!pendingFromStore) {
          showError('Please select delivery option');
          return;
        }
        setSelectedSendOption(pendingFromStore);
        const targetKey = returnToStep || "reviewAndEditStep";
        const targetIndex = steps.findIndex(s => s.key === targetKey);
        if (targetIndex !== -1) {
          setReturnToStep(null);
          setCurrentStep(targetIndex);
          return;
        }
      } else if (steps[currentStep].key === "reviewAndEditStep") {
        setReviewStepApiError(null);
        // Call GetFieldsForProduct on Continue from Review step
        try {
          const opt: any = pendingSendOption || selectedSendOptionValue || {};
          const amountFromState = Number(String(currentSendAmount || "0").replace(/,/g, ""));
          const amountFromOption = Number(opt?.SendAmounts?.SendAmount ?? 0);
          const payload: any = {
            unitProfileID: unitProfileId || "",
            agentID: agentId || "",
            receiveCountry: selectedDestinationCountryCode || opt?.DestinationCountry || "",
            deliveryOption: opt?.DeliveryOption || "",
            thirdPartyType: "NONE",
            receiveCurrency: selectedReceiveCurrency || opt?.ValidReceiveCurrency || opt?.EstimatedReceiveCurrency || "",
            amount: isFinite(amountFromOption) && amountFromOption > 0 ? amountFromOption : (isFinite(amountFromState) ? amountFromState : 0),
            sendCurrency: opt?.SendAmounts?.SendCurrency || "USD",
            productType: ProductType.SEND,
            CustomerId: "0",
            receiveAgentID: opt?.ReceiveAgentId ?? (opt as any)?.receiveAgentID ?? opt?.ReceiveAgentID ?? undefined,
          };
          const gffpResponse : FieldsForProductInfo = await getFieldsForProductRequest(payload);
          const requiredFields: any[] = GffpHelper.filterRequiredAndOptionalFields(gffpResponse) as any[];

          // Group by FieldCategory with full field objects
          const groupedByCategory: Record<string, any[]> = requiredFields.reduce((acc: Record<string, any[]>, field: any) => {
            const category = field.FieldCategory ?? field.fieldCategory ?? 'Uncategorized';
            if (!acc[category]) acc[category] = [];
            acc[category].push(field);
            return acc;
          }, {});

          // Stable sort within each category by DisplayOrder (if present)
          Object.keys(groupedByCategory).forEach((cat) => {
            groupedByCategory[cat].sort((a, b) => {
              const ao = a.DisplayOrder ?? a.displayOrder ?? 0;
              const bo = b.DisplayOrder ?? b.displayOrder ?? 0;
              return ao - bo;
            });
          });

          const distinctCategories = Object.keys(groupedByCategory);

          try { setGffpFieldsByCategory?.(groupedByCategory); } catch {}
          try { setGffpCategories?.(distinctCategories); } catch {}
        } catch (err: any) {
          console.error("getFieldsForProductRequest failed:", err);
          const errMsg = err?.message || "Unable to load required fields. Please try again.";
          setReviewStepApiError(String(errMsg));
          try { showError(String(errMsg)); } catch {}
          return; // stay on review step on failure
        }

        captureInitialReviewWithPromoCodeEvent(promoCode, isPromocodeApplied);
        // setIsGradientExpand(false)
        // Success → proceed to next step
        const targetIndex = steps.findIndex(s => s.key === "senderName");
        if (targetIndex !== -1) {
          setCurrentStep(targetIndex);
          return;
        }
      } else if (steps[currentStep].key === "senderName") {
        const isValid = await SenderFullNameStepRef.current?.submit();
        if (!isValid) return; // stay on same step
        captureSenderFullnameEvent(senderFirstName, senderMiddleName, senderLastName)
        const targetIndex = steps.findIndex(s => s.key === "senderAddress");
        if (targetIndex !== -1) {
          setCurrentStep(targetIndex);
          return;
        }
      } else if (steps[currentStep].key === "senderAddress") {
        const isValid = await SenderAddressStepRef.current?.submit();
        if (!isValid) return; // stay on same step
        captureSenderAddressEvent(senderAddress, senderCity, senderZipCode, senderState)
        const targetIndex = steps.findIndex(s => s.key === "senderDOB");
        if (targetIndex !== -1) {
          setCurrentStep(targetIndex);
          return;
        }
      } else if (steps[currentStep].key === "senderDOB") {
        const isValid = await SenderDOBStepRef.current?.submit();
        if (!isValid) return; // stay on same step
        captureSenderDOBEvent(storedDobString);
        setIsDOBCOnfirm(true)

        // const targetIndex = steps.findIndex(s => s.key === "senderIdentification");
        // if (targetIndex !== -1) {
        //   setCurrentStep(targetIndex);
        //   return;
        // }
      } else if (steps[currentStep].key === "senderIdentification") {
        // Fresh read from Zustand so we always use the ID type just written by SenderIdentificationTypeStep.
        const session = useSessionStore.getState() as any;
        const selectedTypeKey = session.selectedIdTypeKey as string | null | undefined;
        const idPhotoStateAbbr = session.selectedIdPhotoIdStateAbbr as string | null | undefined;

        captureSenderIdType(
          selectedTypeKey ?? "",
          session.selectedIdTypeValue ?? "",
          session.selectedIdTypeLabel ?? "",
          session.selectedIdPhotoIdCountry ?? "",
          idPhotoStateAbbr ?? "",
          session.selectedIdPhotoIdStateName ?? ""
        );

        // Mexican Electoral and Passport skip Choose State of ID (all other types go through stateOfId).
        const skipChooseStateOfId =
          selectedTypeKey === MEXICAN_ELECTORAL_ID_TYPE_KEY ||
          selectedTypeKey === PASSPORT_ID_TYPE_KEY;
        const nextKey = skipChooseStateOfId ? "senderIdNumber" : "stateOfId";
        const targetIndex = steps.findIndex(s => s.key === nextKey);
        if (targetIndex !== -1) {
          try { recordStepTransition?.(nextKey); } catch {}
          setCurrentStep(targetIndex);
          return;
        }
      } else if (steps[currentStep].key === "stateOfId") {
        // captureSenderIdType()
        const targetIndex = steps.findIndex(s => s.key === "senderIdNumber");
        if (targetIndex !== -1) {
          try { recordStepTransition?.("senderIdNumber"); } catch {}
          setCurrentStep(targetIndex);
          return;
        }
      } else if (steps[currentStep].key === "senderIdNumber") {
        const isValid = await SenderIdentificationStepRef.current?.submit();
        if (!isValid) return; // stay on same step
        captureSenderIdentificationNumbervent(senderPhotoIdNumber)
        const targetIndex = steps.findIndex(s => s.key === "receiverName");
        if (targetIndex !== -1) {
          try { recordStepTransition?.("receiverName"); } catch {}
          setCurrentStep(targetIndex);
          return;
        }
      } else if (steps[currentStep].key === "receiverName") {
        const isValid = await ReceiverFullNameStepRef.current?.submit();
        if (!isValid) return; // stay on same step
        // After receiver name, check for any missing required fields from GFFP
        captureReceiverNameEvent(storeFirst, storeMiddle, storeLast, storeLast2);
        setIsReceiverFullnameConfirm(true)
      } else if (steps[currentStep].key === "fraudPrevention") {
        // Build and log update consumer profile draft before proceeding
        captureFraudPreventionEvent();
        const targetIndex = steps.findIndex(s => s.key === "finalReview");
        if (targetIndex !== -1) {
          try { recordStepTransition?.("finalReview"); } catch {}
          setCurrentStep(targetIndex);
          return;
        }
      } else if (steps[currentStep].key === "finalReview") {
        captureFinalReviewEvent();
        // If this is a new consumer, send them to the PIN step before proceeding
        if (isNewConsumer) {
          const pinIndex = steps.findIndex(s => s.key === "pin");
          if (pinIndex !== -1) {
            try { recordStepTransition?.("pin"); } catch { }
            setCurrentStep(pinIndex);
            return;
          }
        }
        else {
          // For existing consumers, perform update/validate/post flow here.
          // If validation reports missing fields, redirect to Additional Information.
          try {
            const result = await maultipleCalls();
            if (result && (result as any).error) {
              setFinalReviewApiError((result as any).errorMessage ?? 'An error occurred.');
              return;
            }
            if (result && (result as any).missingFieldsFound) {
              skippedAdditionalInformationBeforeFraudRef.current = false;
              additionalInformationNavSourceRef.current = "finalReview";
              const aiIndex = steps.findIndex(s => s.key === "additionalInformation");
              if (aiIndex !== -1) {
                setCurrentStep(aiIndex);
                return;
              }
            }
          } catch (e) {
            console.warn('maultipleCalls failed', e);
            setFinalReviewApiError(String((e as any)?.message ?? 'An error occurred.'));
            return;
          }
        }

        const targetIndex = steps.findIndex(s => s.key === "headToCashier");
        if (targetIndex !== -1) {
          try { recordStepTransition?.("headToCashier"); } catch {}
          setCurrentStep(targetIndex);
          return;
        }
      } else if (steps[currentStep].key === "additionalInformation") {
        // After entering additional info
        if (returnToStep) {
          // Path when we intentionally inserted Additional Information mid-flow (e.g., after receiverName)
          const targetIndex = steps.findIndex(s => s.key === returnToStep);
          if (targetIndex !== -1) {
            try { recordStepTransition?.(returnToStep); } catch {}
            additionalInformationNavSourceRef.current = null;
            setCurrentStep(targetIndex);
            setReturnToStep(null);
            return;
          }
        } else {
          // Path when we arrived here from finalReview due to validation requiring more fields
          try {
            const result = await maultipleCalls();
            if (result && (result as any).error) {
              // An error was shown; stay on current step
              return;
            }
            if (result && (result as any).missingFieldsFound) {
              // still missing (should be rare if validations pass)
              return;
            }
          } catch (e) {
            console.warn('maultipleCalls from additionalInformation failed', e);
            return;
          }
          const targetIndex = steps.findIndex(s => s.key === "headToCashier");
          if (targetIndex !== -1) {
            try { recordStepTransition?.("headToCashier"); } catch {}
            additionalInformationNavSourceRef.current = null;
            setCurrentStep(targetIndex);
            return;
          }
        }
      } else if (steps[currentStep].key === "pinCreated") {
        capturePinCreatedEvent();
        // Check if we're in reset PIN flow
        const isResetPinFlow = steps.some(s => s.key === "otp");
        
        if (isResetPinFlow) {
          // After PIN is created in reset flow, return to main existing consumer flow
          // Reset to BASE_STEPS_EXISTING and navigate to country step
          setSteps(BASE_STEPS_EXISTING);
          setSendCashStepsMode?.("existing");
          const countryIndex = BASE_STEPS_EXISTING.findIndex(s => s.key === "country");
          if (countryIndex !== -1) {
            try { recordStepTransition?.("country"); } catch {}
            setCurrentStep(countryIndex);
            return;
          }
        } else {
          // Normal flow: go to headToCashier
          const targetIndex = steps.findIndex(s => s.key === "headToCashier");
          if (targetIndex !== -1) {
            try { recordStepTransition?.("headToCashier"); } catch {}
            setCurrentStep(targetIndex);
            return;
          }
        }
      }
      else if (steps[currentStep].key === "headToCashier") {
        // Finish: allow host app to handle navigation back to dashboard with kiosk token
        try { handleFinish?.(); } catch {}
        const targetIndex = steps.findIndex(s => s.key === "headToCashier");
        if (targetIndex !== -1) {
          setCurrentStep(targetIndex);
          return;
        }
      }
      // if (returnToStep !== null) {
      //   const returnToStepIndex = steps.findIndex(s => s.key === "reviewAndEditStep");
      //   console.log("returnToStep✅ Flow completed", returnToStepIndex);
      //   setCurrentStep(returnToStepIndex);        // go back to the remembered step
      //   setReturnToStep(null);               // clear so next time is normal flow
      // } else {
      //   console.log("rELEELse eturnToStep✅ Flow completed", currentStep);
      //   setCurrentStep(prev => prev + 1);    // normal forward navigation
      // }
      console.log("✅ Flow completed");
    }, 'Processing...');
  };

  const handleSkip = () => {
    // Run validation + transaction only, then jump to HeadToCashier
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    executeWithLoading(async () => {
      try {
        const result = await maultipleCalls({ skipUpdateProfile: true });
        if (result && (result as any).error) {
          // An error was shown; stay on current step
          return;
        }
      } catch (e) {
        console.warn('Skip flow failed', e);
      }
      const targetIndex = steps.findIndex(s => s.key === "headToCashier");
      if (targetIndex !== -1) {
        try { recordStepTransition?.("headToCashier"); } catch {}
        setCurrentStep(targetIndex);
      }
    }, 'Processing...');
  };

  const maultipleCalls = async (options?: { skipUpdateProfile?: boolean }) => {
    const skipUpdate = !!options?.skipUpdateProfile;
    try {
      let effectiveConsumerId: string | undefined = undefined;
      if (!skipUpdate) {
        const profile: SendMoneyPostUpdateConsumerProfileParameter = logUpdateConsumerProfileDraft();
        profile.unitProfileID = unitProfileId || "";
        profile.agentID = agentId || "";
        const profileResponse = await updateConsumerProfile(profile);
        try {
          effectiveConsumerId = (profileResponse && (profileResponse.consumerId || profileResponse.ConsumerId || profileResponse.id || profileResponse.Id)) ?? undefined;
        } catch {}
      } else {
        // For skip flow, attempt to use existing consumerId from store; fall back to "0"
        try { effectiveConsumerId = consumerIdFromStore ? String(consumerIdFromStore) : "0"; } catch { effectiveConsumerId = "0"; }
      }

      try {
        const validationReq = buildSendValidationRequestFromStore(String(effectiveConsumerId ?? "0"));
        // check what are the Req, OPT fields are missing in the validationReq from requiredFields
        // get the gffpFieldsByCategory from the store
        const gffpFieldsByCategory = useSessionStore.getState().gffpFieldsByCategory || {};
        const missingRequiredFields = filterSendFlowMissingGffpFields(
          new RequiredFields().getMissingFields(gffpFieldsByCategory, validationReq)
        );
        if (missingRequiredFields && missingRequiredFields.length > 0) {
          try { setMissingRequiredFields?.(missingRequiredFields as any); } catch {}
          return { missingFieldsFound: true } as const;
        }
        const validationResponse = await validateConsumer(validationReq);
        // Inspect potential error envelope in validation response
        try {
          const errDetail = extractErrorDetailFromValidateConsumerResponse(validationResponse);
          if (errDetail) {
            const msg = formatValidateConsumerErrorMessage(errDetail);
            try { showError?.(msg); } catch {}
            return { error: true, errorMessage: msg } as const;
          }
        } catch {}
        try {
          const ref = (validationResponse && ((validationResponse.confirmationNumber) || (validationResponse.ConfirmationNumber) || (validationResponse.referenceNumber) || (validationResponse.ReferenceNumber))) ?? undefined;
          if (ref) {
            try { setConfirmationNumber?.(String(ref)); } catch {}
            try { setFinalSendValidationResponseStore?.(validationResponse); } catch {}
          }
        } catch {}
        // Build transaction parameters and post to kiosk
        try {
          const txParams = buildTransactionParametersFromStore(validationResponse, 'send',
            validationResponse.MgiTransactionSessionId);
          const txResp = await postTransactionToKiosk(txParams);
        } catch (e) {
          console.warn('Failed to post transaction to kiosk', e);
        }
      } catch (e) {
        console.warn("Failed to build send validation request", e);
        const msg = String((e as any)?.message || 'Validation failed. Please try again.');
        try { showError?.(msg); } catch {}
        return { error: true, errorMessage: msg } as const;
      }

    } catch (e) {
      console.warn("maultipleCalls failed", e);
      const msg = String((e as any)?.message || 'Something went wrong. Please try again.');
      try { showError?.(msg); } catch {}
      return { error: true, errorMessage: msg } as const;
    }
  }

  // Enable Continue button; steps that manage their own validation (sendingAmountEntry, howTheyReceive) must not be defaulted to true
  useEffect(() => {
    const key = steps[currentStep]?.key;
    if (key === "phone") {
      setIsStepValid(false);
    } else if (key === "sendingAmountEntry" || key === "howTheyReceive") {
      // Let the step control validity via onValidationChange; start false so Continue is blocked until valid
      setIsStepValid(false);
    } else {
      setIsStepValid(true);
    }
    if (key === "reviewAndEditStep") {
      setReviewStepApiError(null);
    }
    if (key === "finalReview") {
      setFinalReviewApiError(null);
    }
    if (key === "pin" || key === "confirmPin") {
      setPinStepApiError(null);
      setConfirmPinApiError(null);
    }
  }, [currentStep]);

  const handleBack = () => {
    if (currentStep <= 0) return;
    const curKey = steps[currentStep]?.key;
    const prevIdx = currentStep - 1;
    const session = useSessionStore.getState() as any;
    const selectedTypeKey = session.selectedIdTypeKey as string | null | undefined;
    if (
      curKey === "senderIdNumber" &&
      steps[prevIdx]?.key === "stateOfId" &&
      (selectedTypeKey === MEXICAN_ELECTORAL_ID_TYPE_KEY ||
        selectedTypeKey === PASSPORT_ID_TYPE_KEY)
    ) {
      const idIdx = steps.findIndex((s) => s.key === "senderIdentification");
      if (idIdx !== -1) {
        setCurrentStep(idIdx);
        return;
      }
    }
    const destCountry = selectedDestinationCountryCode;
    const isUsOrCaDest =
      destCountry === "USA" || destCountry === "CAN";
    if (
      curKey === "sendingAmountEntry" &&
      steps[prevIdx]?.key === "state" &&
      !isUsOrCaDest
    ) {
      const countryIdx = steps.findIndex((s) => s.key === "country");
      if (countryIdx !== -1) {
        try { recordStepTransition?.("country"); } catch {}
        setCurrentStep(countryIdx);
        return;
      }
    }
    if (
      curKey === "fraudPrevention" &&
      steps[prevIdx]?.key === "additionalInformation" &&
      skippedAdditionalInformationBeforeFraudRef.current
    ) {
      const receiverIdx = steps.findIndex((s) => s.key === "receiverName");
      if (receiverIdx !== -1) {
        try { recordStepTransition?.("receiverName"); } catch {}
        setCurrentStep(receiverIdx);
        return;
      }
    }
    if (curKey === "additionalInformation") {
      const src = additionalInformationNavSourceRef.current;
      if (src === "finalReview") {
        const frIdx = steps.findIndex((s) => s.key === "finalReview");
        if (frIdx !== -1) {
          try { recordStepTransition?.("finalReview"); } catch {}
          additionalInformationNavSourceRef.current = null;
          setCurrentStep(frIdx);
          return;
        }
      }
      if (src === "receiverReturnToFraud") {
        const rnIdx = steps.findIndex((s) => s.key === "receiverName");
        if (rnIdx !== -1) {
          try { recordStepTransition?.("receiverName"); } catch {}
          setReturnToStep(null);
          additionalInformationNavSourceRef.current = null;
          setCurrentStep(rnIdx);
          return;
        }
      }
    }
    setCurrentStep((prev) => prev - 1);
  };

  const handleCountryEdit = () => {
    const targetIndex = steps.findIndex(s => s.key === "country");
    if (targetIndex !== -1) {
      setCurrentStep(targetIndex);
    }
  };

  const handleCustomerNotFound = () => {
    const targetIndex = steps.findIndex(s => s.key === "country");
    if (targetIndex !== -1) {
      setCurrentStep(targetIndex);
    }
  };

  const handleForgotPassword = () => {
    console.log('handleForgotPassword currentStep==>', currentStep)
    // mark as new consumer so finalReview routes to PIN
    setIsNewConsumer?.(true);
    // Close pop up
    setShowPopup(false);
    setLocked(false);

    //find index from the NEW flow, not the current one
    const newFlow = BASE_STEPS_NEW;
    const countryIndex = newFlow.findIndex(s => s.key === 'country');

    // update both pieces of state together
    setSteps(newFlow);
    setSendCashStepsMode?.("new");
    if (countryIndex !== -1) {
      setCurrentStep(countryIndex);
    }
  }

  const handleResetPin = () => {
    console.log('handleResetPin - Starting reset PIN flow');
    // Close the account locked modal
    setShowPopup(false);
    setLocked(false);
    
    // Set steps to reset PIN flow: OTP → Enter New PIN → Confirm PIN → PIN Created
    setSteps(RESET_PIN_STEPS);
    setSendCashStepsMode?.("resetPin");
    
    // Navigate to OTP step (first step in reset PIN flow)
    const otpIndex = RESET_PIN_STEPS.findIndex(s => s.key === 'otp');
    if (otpIndex !== -1) {
      setCurrentStep(otpIndex);
    }
  }
  

  // // Jumps directly to a step by key
  // const handleEditStep = (key: string) => {
  //   const targetIndex = steps.findIndex(s => s.key === key);
  //   if (targetIndex !== -1) {
  //     setCurrentStep(targetIndex);
  //   }
  // };

  // const handleClose = () =>{

  // }

  const closeNeedHelp = () => {
    setNeedHelp(false);
  }

  const handleNeedHelpCall = () => {
    // Calling to provided number
  }

  const currentKey = steps[currentStep]?.key;
  const isBaseNew = steps === BASE_STEPS_NEW; // still only true if literally the same array
  const hideContinueForPin = currentKey === "pin" && isBaseNew;

  const showContinueButton =
    ![
      "country",
      "state",
      "howTheyReceive",
      "stateOfId",
      "reviewAndEditStep",
      "finalReview",
      "pin",
      "confirmpin",
    ].includes(currentKey) ||
    hideContinueForPin === false; // show only if not in list and not hiding for pin

  const formatDateForPOPup = () => {
    try {
      // Always read latest from store to avoid stale closures
      const { senderDOB: dobFromState, senderDOBString } = useSessionStore.getState() as any;
      
      // Prefer string format to avoid timezone issues from Date objects
      // This ensures consistent display regardless of how the date was originally parsed
      const rawString = (typeof dobFromState === 'string' ? dobFromState : undefined) || (senderDOBString as string | undefined) || (senderDOBStringFromStore as string | undefined);
      
      let date: Date | undefined = undefined;
      
      if (rawString) {
        const s = String(rawString).trim();
        if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
          // YYYY-MM-DD or ISO; create at noon to avoid TZ shifts
          const y = Number(s.slice(0, 4));
          const m = Number(s.slice(5, 7)) - 1;
          const d = Number(s.slice(8, 10));
          date = new Date(y, m, d, 12, 0, 0, 0);
        } else if (/^(\d{2})\/(\d{2})\/(\d{4})$/.test(s)) {
          // Handle both MM/DD/YYYY and DD/MM/YYYY
          const [, p1, p2, yyyy] = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/) as RegExpMatchArray;
          const n1 = Number(p1);
          const n2 = Number(p2);
          let mm = n1;
          let dd = n2;
          // If first part > 12, it's DD/MM/YYYY; swap
          if (n1 > 12 && n2 <= 12) {
            mm = n2;
            dd = n1;
          }
          // Create date at noon local time to avoid timezone boundary issues
          date = new Date(Number(yyyy), mm - 1, dd, 12, 0, 0, 0);
        } else {
          const tmp = new Date(s);
          if (!isNaN(tmp as any)) date = tmp;
        }
      }
      
      // Fallback to Date object from store if string is not available
      if (!date) {
        const asDate: any = dobFromState instanceof Date ? dobFromState : undefined;
        date = asDate && !isNaN(asDate as any) ? (asDate as Date) : undefined;
      }

      if (!date || isNaN(date as any)) return '';
      return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    } catch {
      return '';
    }
  };

  const handleDOBNavigation = () => {
    captureSenderDOBConfirmationEvent(storedDobString);
    const targetIndex = steps.findIndex(s => s.key === "senderIdentification");
    if (targetIndex !== -1) {
      setCurrentStep(targetIndex);
      return;
    }
  }

  // Calculate dynamic fee data for gradient footer using same logic as ReviewAndEditStep and ReviewFinalStep
  const getGradientFeeData = () => {
    // For finalReview step, use the same logic as ReviewFinalStep
    if (steps[currentStep].key === "finalReview") {
      const getValuesFromLatestFeeLookupResponse = lastFeeLookupResponse?.FeeInfos?.find((f: any) => {
        const sameDisplay = (f?.DeliveryOption || '') === (selectedDeliveryOptionStore?.DeliveryOption || '');
        return sameDisplay;
      });
 
      if (!getValuesFromLatestFeeLookupResponse) {
        return {
          exchangeRate: 0,
          fees: 0,
          total: 0,
          sendCurrency: 'USD',
          receiveCurrency: '—',
          totalSendTax: 0
        };
      }
 
      const sendCurrency: string = getValuesFromLatestFeeLookupResponse?.SendAmounts?.SendCurrency || "USD";
      const receiveCurrency: string = selectedReceiveCurrency || (getValuesFromLatestFeeLookupResponse ? FeeLookupHelper.getReceiveCurrency(getValuesFromLatestFeeLookupResponse) : "");
      const exchangeRateNum: number = getValuesFromLatestFeeLookupResponse ? FeeLookupHelper.getExchangeRate(getValuesFromLatestFeeLookupResponse) : 0;
      const fees: number = Number(getValuesFromLatestFeeLookupResponse?.SendAmounts?.TotalSendFees || 0);
      const total: number = Number(getValuesFromLatestFeeLookupResponse?.TotalAmount || 0);
      const totalSendTax: number = Number(getValuesFromLatestFeeLookupResponse?.SendAmounts?.TotalSendTaxes || 0);
 
      return {
        exchangeRate: exchangeRateNum,
        fees,
        total,
        sendCurrency,
        receiveCurrency,
        totalSendTax
      };
    }
 
    // For reviewAndEditStep, use the existing logic
    const option: any = pendingSendOption || selectedSendOption || null;
    
    if (!option) {
      return {
        exchangeRate: 0,
        fees: 0,
        total: 0,
        sendCurrency: 'USD',
        receiveCurrency: '—',
        totalSendTax: 0
      };
    }
 
    const sendCurrency: string = option?.SendAmounts?.SendCurrency || 'USD';
    const sendAmount: number = Number(option?.SendAmounts?.SendAmount || 0);
    const receiveCurrency: string = option?.ReceiveAmounts?.ValidCurrencyIndicator
      ? (option?.ValidReceiveCurrency || '')
      : (option?.EstimatedReceiveCurrency || option?.ValidReceiveCurrency || '');
    const receiveAmount: number = Number(option?.ValidReceiveAmount ?? option?.EstimatedReceiveAmount ?? 0);
    const exchangeRateNum: number = Number(FeeLookupHelper.getExchangeRate(option) || 0);
    const fees: number = Number(option?.SendAmounts?.TotalDiscountAmount || 0) + Number(option?.SendAmounts?.TotalSendFees || 0);
    const totalBase: number = Number(option?.TotalAmount || 0);
    const total: number = totalBase;
    const totalSendTax: number = Number(option?.SendAmounts?.TotalSendTaxes || 0);
 
    return {
      exchangeRate: exchangeRateNum,
      fees,
      total,
      sendCurrency,
      receiveCurrency,
      totalSendTax
    };
  };

  const renderDOBPopUp = () => (
        <View style={styles.root}>
          <TouchableOpacity
            style={styles.scrim}
            activeOpacity={1}
            onPress={() => setIsDOBCOnfirm(false)}
          />
          <View style={styles.bottomSheet}>
            <TouchableOpacity style={styles.modalHeaderRow}
              onPress={() => { setIsDOBCOnfirm(false); }}
              testID="senderDOBClose">
              <CloseIcon width={24} height={24}/>
            </TouchableOpacity>
            <View style={{ alignItems: 'center' }}>
              <CalenderContainer width={64} height={64}/>
              {/* <Image
                  source={CalenderContainer}
                  style={{ width: 64, height: 64, borderRadius: 50}}
                /> */}
              <Text style={styles.modalTitle}>{t("SENDER_DOB_CONFIRMATION_TITLE")}</Text>
              {
                i18n.language === 'en' ? (
                  <Text style={styles.modalInfo}>
                    Make sure your birthday matches your <Text style={[styles.modalInfo, { color: Colors.black, fontWeight: '700', paddingHorizontal: 0}]}>legal ID</Text> to avoid money transfer delays.
                  </Text>
                  ) :
                  <Text style={styles.modalInfo}>
                  {t("SENDER_DOB_CONFIRM_SUBTITLE")}
                </Text>
              }
              {/* <Text style={styles.modalInfo}>
                {t("SENDER_DOB_CONFIRM_SUBTITLE")}
              </Text> */}
              <Text style={styles.modalDOB}>{formatDateForPOPup()}</Text>
            </View>
            <View style={styles.footer}>
              <TouchableOpacity
                testID="senderDOBConfirm"
                style={styles.confirmBtn}
                onPress={() => { setIsDOBCOnfirm(false); handleDOBNavigation(); }}
              >
                <Text style={[styles.cancelText, { color: Colors.white}]}>{t("SENDER_DOB_CONFIRM")}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                testID="senderDOBEdit"
                style={styles.cancelBtn}
                onPress={() => setIsDOBCOnfirm(false)}
              >
                <Text style={styles.cancelText}>{t("EDIT")}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
  )

  const renderReceiverInfoConfirmationPopUp = () => (
        <View style={styles.root}>
          <TouchableOpacity
            style={styles.scrim}
            activeOpacity={1}
            onPress={() => setIsReceiverFullnameConfirm(false)}
          />
          <View style={styles.bottomSheet}>
                  <TouchableOpacity style={styles.modalHeaderRow}
              onPress={() => { setIsReceiverFullnameConfirm(false); }}
              testID="senderDOBClose">
                <CloseIcon width={24} height={24}/>
            </TouchableOpacity>
             <View style={{ alignItems: 'center' }}>
              <GroupId width={64} height={64}/>
              {/* <Image
                  source={GroupId}
                  style={{ width: 64, height: 64, borderRadius: 50}}
                /> */}
              <Text style={styles.modalTitle}>{t("IS_THIS_THEIR_LEGAL_NAME")}</Text>
              <Text style={styles.modalInfo}>
                {t("MAKE_SURE_THEIR_NAME_MATCHES")}
              </Text>
              <View style={styles.receiverLegalNameContainer}>
                <Text style={styles.receiverLegalNameText}>
                  {receiverFirstName} {receiverLastName}
                </Text>
              </View>
            </View>
            <View style={styles.footer}>
      
                  <TouchableOpacity testID="receiverFullNameConfirm"
                    style={styles.confirmBtn}
                    onPress={() => {setIsReceiverFullnameConfirm(false); handleReceiverInformation()}}
                  >
                    <Text style={[styles.cancelText, { color: Colors.white}]}>{t("CONFIRM")}</Text>
                  </TouchableOpacity>
      
                  <TouchableOpacity testID="receiverFullNameEdit"
                    style={styles.cancelBtn}
                    onPress={() => setIsReceiverFullnameConfirm(false)}
                  >
                    <Text style={styles.cancelText}>{t("EDIT")}</Text>
                  </TouchableOpacity>
                  </View>
          </View>
        </View>
  )

  const effectiveHeaderHeight = steps[currentStep].key === "pinCreated" ? 0 : headerHeight;
  const scrollAreaHeight =
    footerHeight > 0 && layoutHeight > 0
      ? layoutHeight - effectiveHeaderHeight - footerHeight
      : undefined;
  // Country/state steps have no footer; list can use full height below header
  const countryStateAreaHeight =
    layoutHeight > 0 && effectiveHeaderHeight >= 0
      ? layoutHeight - effectiveHeaderHeight
      : undefined;

  const isReviewOrFinalReviewScroll =
    steps[currentStep].key === "reviewAndEditStep" || steps[currentStep].key === "finalReview";

  const stickFooterAboveKeyboard = sendStickyKeyboardContinueKeys.has(
    steps[currentStep]?.key ?? ""
  );

  return (
    <KeyboardAvoidingView
      style={[
        styles.container,
        layoutHeight > 0 && { height: layoutHeight },
        /* Web: height is already visualViewport.height (area above keyboard). Do not add
         * paddingBottom from useWebKeyboardHeight — that double-counts and leaves a gap under
         * the footer while cropping the scroll area. */
      ]}
      behavior={
        Platform.OS === "web"
          ? undefined
          : stickFooterAboveKeyboard
            ? "padding"
            : undefined
      }
      enabled={Platform.OS === "web" ? true : stickFooterAboveKeyboard}
      keyboardVerticalOffset={Platform.OS === "ios" && stickFooterAboveKeyboard ? 60 : 0}
    >
       <View style={styles.mainWrapper}>
        {(steps[currentStep].key !== "pinCreated") && (
        <>
          <View
            style={[styles.header, Platform.OS === "web" && styles.headerFixed]}
            onLayout={(e: LayoutChangeEvent) => setHeaderHeight(e.nativeEvent.layout.height)}
          >
          <View style={styles.topBar}>
            <View
              style={[
                styles.topBarSide,
                steps[currentStep].key === "phone" && styles.topBarSideHidden,
              ]}
            >
              {steps[currentStep].key !== "phone" ? (
                <TouchableOpacity style={styles.topBarButton} onPress={handleBack} testID="backButton">
                  <BackIcon width={24} height={24} />
                </TouchableOpacity>
              ) : null}
            </View>
            <View style={styles.topBarCenter}>
              {steps[currentStep].key === "sendingAmountEntry" ? (
                <TouchableOpacity
                  style={styles.topBarCountry}
                  onPress={handleCountryEdit}
                  testID="countryButton"
                >
                  <View style={styles.topBarCountryFlag}>
                    <Flag iso3={selectedDestinationCountryCode ?? ""} width={16} height={16} />
                  </View>
                  <Text
                    style={styles.selectedCountryText}
                    numberOfLines={1}
                    ellipsizeMode="tail"
                  >
                    {selectedCountryName}
                  </Text>
                  <View style={styles.topBarCountryChevron}>
                    <DownArrow width={16} height={16} />
                  </View>
                </TouchableOpacity>
              ) : null}
            </View>
            <View style={styles.topBarActions}>
              <TouchableOpacity onPress={() => setNeedHelp(true)} style={styles.topBarButton} testID="helpButton">
                <HelpIcon width={24} height={24} />
              </TouchableOpacity>
              <TouchableOpacity style={styles.topBarButton} onPress={handleClose} testID="closeButton">
                <CloseIcon width={24} height={24} />
              </TouchableOpacity>
            </View>
          </View>
          <View style={styles.progressRow}>
            {steps.map((_, i) => (
              <View
                key={i}
                style={[
                  styles.progressSegment,
                  { borderTopLeftRadius: i === 0 ? 9 : 0 },
                  { borderBottomLeftRadius: i === 0 ? 9 : 0 },
                  { borderTopRightRadius: i === steps.length - 1 ? 9 : 0 },
                  { borderBottomRightRadius: i === steps.length - 1 ? 9 : 0 },
                  { backgroundColor: i <= currentStep ? Colors.titleTextColor : Colors.searchBackground },
                ]}
              />
            ))}
          </View>
        </View>
          {Platform.OS === "web" && (
            <View style={[styles.headerSpacer, headerHeight > 0 && { height: headerHeight }]} />
          )}
        </>
        )}
        {(steps[currentStep].key === "country" || steps[currentStep].key === "state" || steps[currentStep].key === "stateOfId" || steps[currentStep].key === "howTheyReceive" || steps[currentStep].key === "senderIdNumber" || steps[currentStep].key === "receiverName") ? (
          (() => {
            const stepAreaHeight = (steps[currentStep].key === "howTheyReceive" || steps[currentStep].key === "senderIdNumber" || steps[currentStep].key === "receiverName") ? scrollAreaHeight : countryStateAreaHeight;
            const hasHeight = stepAreaHeight != null && stepAreaHeight > 0;
            return (
          <View
            style={[
              styles.stepAreaContainer,
              hasHeight && { height: stepAreaHeight, maxHeight: stepAreaHeight },
              !hasHeight && Platform.OS === 'web' && styles.stepAreaContainerWebFallback,
            ]}
          >
            {steps[currentStep].key === "country" ? (
              <AnyStepComponent
                testID="step-country"
                onCountrySelected={(code?: string) => handleNext(code)}
              />
            ) : steps[currentStep].key === "state" ? (
              <AnyStepComponent
                testID="step-state"
                onCountrySelected={(code?: string) => handleNext(code)}
              />
            ) : steps[currentStep].key === "stateOfId" ? (
              <AnyStepComponent
                testID="step-stateOfId"
                onCountrySelected={(code?: string) => handleNext(code)}
              />
            ) : steps[currentStep].key === "howTheyReceive" ? (
              <AnyStepComponent
                testID="step-howTheyReceive"
                onCountrySelected={handleNext}
                onValidationChange={handleValidationChange}
              />
            ) : steps[currentStep].key === "senderIdNumber" ? (
              <AnyStepComponent
                testID="step-senderIdNumber"
                ref={SenderIdentificationStepRef}
                onValidationChange={handleValidationChange}
              />
            ) : (
              <AnyStepComponent
                testID="step-receiverName"
                ref={ReceiverFullNameStepRef}
                onValidationChange={handleValidationChange}
              />
            )}
          </View>
            );
          })()
        ) : (
        <ScrollView
          ref={scrollRef}
          style={[
            styles.scrollView,
            scrollAreaHeight != null && scrollAreaHeight > 0 && { maxHeight: scrollAreaHeight },
            isReviewOrFinalReviewScroll && { backgroundColor: Colors.white },
            steps[currentStep].key === "senderAddress" && styles.dropdownPriorityScrollLayer,
          ]}
          contentContainerStyle={
            isReviewOrFinalReviewScroll
              ? {
                  paddingBottom: isGradientExpand ? 340 : 220,
                  backgroundColor: Colors.white,
                }
              : steps[currentStep].key === "headToCashier"
                ? styles.scrollContainerHeadToCashier
                : steps[currentStep].key === "fraudPrevention"
                  ? styles.scrollContainerFraudPrevention
                  : styles.scrollContainer
          }
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
          bounces={!(steps[currentStep].key === "reviewAndEditStep" || steps[currentStep].key === "finalReview")}
        >
          {steps[currentStep].key === "phone" ? (
            <AnyStepComponent
              testID="step-phone"
              ref={phoneStepRef}
              onValidationChange={handleValidationChange}
            />
          ) : steps[currentStep].key === "senderAddress" ? (
            <AnyStepComponent
              testID="step-senderAddress"
              ref={SenderAddressStepRef}
              onValidationChange={handleValidationChange}
            />
          ) : steps[currentStep].key === "senderName" ? (
            <AnyStepComponent
              testID="step-senderName"
              ref={SenderFullNameStepRef}
              onValidationChange={handleValidationChange}
            />
          ) : steps[currentStep].key === "senderDOB" ? (
            <AnyStepComponent
              testID="step-senderDOB"
              ref={SenderDOBStepRef}
              onValidationChange={handleValidationChange}
            />
          ) : (steps[currentStep].key === "senderIdentification") ? (
            <AnyStepComponent
              testID="step-senderIdentification"
              onContinue={handleNext}
              onValidationChange={handleValidationChange}
            />
          ) : (steps[currentStep].key === "reviewAndEditStep") || (steps[currentStep].key === "finalReview")? (
            <AnyStepComponent
              ref={steps[currentStep].key === "reviewAndEditStep" ? ReviewAndEditStepRef : undefined}
              testID={steps[currentStep].key === "reviewAndEditStep" ? "step-reviewAndEditStep" : "step-finalReview"}
              onEditStep={handleEditStep}
              onContinue={handleNext}
              handleApplyPromoCode={handleApplyPromoCode}
              updatePromocode={updatePromocode}
              clearPromoCode={clearPromoCode}
              promoCode={promoCode}
              errorText={errors.promoCode}
              selectionError={steps[currentStep].key === "reviewAndEditStep" ? (reviewStepApiError ?? undefined) : (finalReviewApiError ?? undefined)}
              />
          ) : (steps[currentStep].key === "fraudPrevention") ? (
            <AnyStepComponent
              testID="step-fraudPrevention"
              onContinue={handleNext}
            />
          ) : (steps[currentStep].key === "otp") ? (
            <AnyStepComponent
              testID="step-otp"
              onContinue={handleNext}
            />
          ) : (steps[currentStep].key === "pin") || (steps[currentStep].key === "confirmPin") ? (
            (() => {
              const isResetPinFlow = steps.some(s => s.key === "otp");
              // In reset PIN flow, PIN step is for entering new PIN (not verification)
              if (isResetPinFlow && steps[currentStep].key === "pin") {
                return (
                  <AnyStepComponent
                    testID="step-pin-reset"
                    attemptsLeft={undefined}
                    locked={false}
                    showAttemptsWarning={false}
                    resetSignal={resetSignal}
                    onContinue={handleNext}
                    apiError={pinStepApiError ?? undefined}
                  />
                );
              }
              // Normal PIN step (verification or new consumer)
              return (
                <AnyStepComponent
                  testID={steps[currentStep].key === "pin" ? "step-pin" : "step-confirmPin"}
                  handleForgotPassword={handleForgotPassword}
                  attemptsLeft={attemptsLeft}
                  locked={locked}
                  showAttemptsWarning={showPinAttemptsWarning}
                  resetSignal={resetSignal}
                  onContinue={handleNext}
                  apiError={steps[currentStep].key === "pin" ? (pinStepApiError ?? undefined) : (confirmPinApiError ?? undefined)}
                />
              );
            })()
          ) : (steps[currentStep].key === "pinCreated") ? (
            <AnyStepComponent
              testID="step-pinCreated"
            />
          ) : (steps[currentStep].key === "headToCashier") ? (
            <AnyStepComponent
              testID="step-headToCashier"
            />
          ) : (steps[currentStep].key === "additionalInformation") ? (
            <AnyStepComponent
              testID="step-additionalInformation"
              onValidationChange={handleValidationChange}
            />) : ( steps[currentStep].key === "sendingAmountEntry" ? (
              <AnyStepComponent
                testID="step-sendingAmountEntry"
                onValidationChange={handleValidationChange}
                cameFromEdit={!!(returnToStep && (returnToStep === 'reviewAndEditStep' || returnToStep === 'finalReview'))} />
            ) : null
          )}
        </ScrollView>
        )}
        <Modal
          visible={needHelp}
          transparent
          animationType="none"
          onRequestClose={() => closeNeedHelp?.()}
        >
          <NeedHelpComponent
            closeNeedHelp={closeNeedHelp}
            handleNeedHelpCall={handleNeedHelpCall}
          />
        </Modal>
        <Modal
          visible={isDOBConfirm}
          transparent
          animationType="none"
          onRequestClose={() => setIsDOBCOnfirm(false)}
        >
          {renderDOBPopUp()}
        </Modal>
         <Modal
          visible={isReceiverFullnameConfirm}
          transparent
          animationType="none"
          onRequestClose={() => setIsReceiverFullnameConfirm(false)}
        >
          {renderReceiverInfoConfirmationPopUp()}
        </Modal>
              <Modal
                visible={showPopup}
                transparent
                animationType="none"
                onRequestClose={() => setShowPopup(false)}
              >
                <View style={styles.modalOverlay}>
                  <View style={styles.bottomSheet}>
                    <TouchableOpacity
                      onPress={() => setShowPopup(false)}
                      style={styles.modalHeaderRow}
                      onFocus={(e: any) => e?.target?.blur?.()}
                    >
                      <CloseIcon width={24} height={24}/>
                    </TouchableOpacity>
                    <View style={{ alignItems:'center', paddingTop: 48}}>
                      <AccountLockedIcon width={64} height={64}/>
                      <Text style={styles.accountLock}>
                        {t("YOUR_ACCOUNT_LOCKED")}
                      </Text>
                      {
                        i18n.language === 'en' ? (
                          <Text style={styles.accountLockInfo}>
                        Select <Text style={{fontWeight: '700'}}>Guest</Text> if you would like to continue or <Text style={{fontWeight: '700'}}>Reset PIN</Text> to link your account
                      </Text>) : (
                        <Text style={styles.accountLockInfo}>
                        {t("YOU_CAN_CONTINUE_AS_GUEST")}
                      </Text>)
                      }
                      
                    </View>
                    <TouchableOpacity
                      style={styles.button}
                      testID="continueAsGuestButton"
                      onPress={() => handleForgotPassword?.()}
                      onFocus={(e: any) => e?.target?.blur?.()}
                    >
                      <Text style={styles.buttonText}>
                      {t("CONTINUE_AS_GUEST")}
                      </Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      testID="resetPinButton"
                      style={styles.cancelBtn}
                      onPress={handleResetPin}
                      onFocus={(e: any) => e?.target?.blur?.()}
                    >
                      <Text style={styles.cancelText}>{t("RESET_PIN")}</Text>
                    </TouchableOpacity>
                  </View>
                </View>
              </Modal>
        { (steps[currentStep].key !== "country" &&
        steps[currentStep].key !== "state")&& 
        steps[currentStep].key !== "stateOfId" &&
        steps[currentStep].key !== "howTheyReceive" &&
        steps[currentStep].key !== "senderIdentification" &&
        steps[currentStep].key !== "reviewAndEditStep" &&
        steps[currentStep].key !== "finalReview" && (
        <View
          style={[
            styles.footerContainer,
            styles.footerShrink,
            steps[currentStep].key === "senderAddress" && styles.footerBehindDropdownLayer,
          ]}
          onLayout={(e: LayoutChangeEvent) => setFooterHeight(e.nativeEvent.layout.height)}
        >
          {(steps[currentStep].key !== "pin" && steps[currentStep].key !== "confirmPin") && (
          <TouchableOpacity
            testID="continueButton"
            style={[
              styles.button,
              { backgroundColor: Colors.secondaryText },
            ]}
            onPress={() => handleNext()}
          >
            <Text style={styles.buttonText}>
              {steps[currentStep].key === "additionalInformation" || (currentStep !== steps.length - 1)
                ? t("STRKEY_CONTINUE")
                : t("I_AM_FINISHED")}
            </Text>
          </TouchableOpacity>)}
          {(steps[currentStep].key === "pin" || steps[currentStep].key === "confirmPin") && !steps.some(s => s.key === "otp") && (
            <TouchableOpacity onPress={steps === BASE_STEPS_EXISTING ? handleForgotPassword : handleSkip } style={styles.secondButton}
            testID="continueAsGuestButton">
              <Text style={styles.contniueAsGuest}>
                {steps === BASE_STEPS_EXISTING ? t("CONTINUE_AS_GUEST") : 'Skip'}
                </Text>
            </TouchableOpacity>
          )}
        </View>
        )}
        {(steps[currentStep].key === "reviewAndEditStep" || steps[currentStep].key === "finalReview") && (() => {
          const feeData = getGradientFeeData();
          return (
            <View
              style={styles.footerShrink}
              onLayout={(e: LayoutChangeEvent) => setFooterHeight(e.nativeEvent.layout.height)}
            >
            <GradientBackground style={styles.gradientFooter}>
              <View style={styles.gradientContent}>
                <TouchableOpacity style={{ padding: 24}} onPress={() => setIsGradientExpand(!isGradientExpand)}>
                  <View style={styles.viewTap}/>
                </TouchableOpacity>
                { isGradientExpand &&
                <View style={{ flex: 1}}>
                  <View style={styles.gradientRow}>
                    <Text style={styles.gradientFeeLabel}>{t("REVIEW_EXCHANGE_RATE")}</Text>
                    <Text style={styles.gradientFeeLabel}>1 {feeData.sendCurrency} = {feeData.exchangeRate.toFixed(4)} {feeData.receiveCurrency}</Text>
                  </View>
                  <View style={styles.gradientRow}>
                    <Text style={styles.gradientFeeLabel}>{t("REVIEW_FEES")}</Text>
                    <View style={{ flexDirection: 'row', alignItems: 'baseline'}}>
                      {/* {(preApplyFees !== null && postApplyFees !== null && Number(preApplyFees.toFixed(2)) !== Number(postApplyFees.toFixed(2))) ? ( */}
                      { isPromocodeApplied ?(
                        <>
                          <Text style={[styles.gradientFeeLabel, { textDecorationLine: 'line-through', marginRight: 8 }]}>{preApplyFees?.toFixed(2)} {selectedDeliveryFeeInfo?.SendAmounts?.SendCurrency}</Text>
                          <Text style={[styles.gradientFeeLabel, { color: Colors.successInfoGreen}]}>{postApplyFees?.toFixed(2)} {selectedDeliveryFeeInfo?.SendAmounts?.SendCurrency}</Text>
                        </>
                      ) : (
                        <Text style={styles.gradientFeeLabel}>{summary.fees.toFixed(2)} {selectedDeliveryFeeInfo?.SendAmounts?.SendCurrency}</Text>
                      )}
                    </View>
                  </View>
                  <View style={styles.gradientRow}>
                    <Text style={styles.gradientFeeLabel}>{t("US_FED_REMITTANCE_TAX")}</Text>
                    <Text style={styles.gradientFeeLabel}>{feeData.totalSendTax.toFixed(2)} {feeData.sendCurrency}</Text>
                  </View>
                </View>
                }
                <View style={styles.gradientRow}>
                  <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                    <WarninBlackIcon width={16} height={16}/>
                    <Text style={[styles.gradientPrice, {marginLeft: 8}]}>{t("REVIEW_TOTAL")}</Text>
                  </View>
                  <Text style={styles.gradientPrice}>{feeData.total.toFixed(2)} {feeData.sendCurrency}</Text>
                </View>
                <TouchableOpacity
                  testID="gradientContinueButton"
                  onPress={() => handleNext()}
                  activeOpacity={0.8}
                  style={styles.gradientContinueButton}
                >
                  <Text style={styles.gradientContinueButtonText}>
                    {steps[currentStep].key === "finalReview" ? t("STRKEY_SUBMIT") : t("STRKEY_CONTINUE")}
                  </Text>
                </TouchableOpacity>
              </View>
            </GradientBackground>
            </View>
          );
        })()}
     </View>
    </KeyboardAvoidingView>
);
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    width: "100%",
    backgroundColor: Colors.white,
    ...(Platform.OS === 'web' && {
      // dvh tracks mobile browser chrome better than vh (iOS Safari URL bar)
      minHeight: '100dvh' as any,
      height: '100dvh' as any,
      maxHeight: '100dvh' as any,
      overflow: 'hidden' as any,
    })
  },
  header: {
    paddingHorizontal: 24,
    flexShrink: 0,
  },
  headerFixed: {
    ...(Platform.OS === "web" && {
      position: "fixed" as any,
      top: 0,
      left: 0,
      right: 0,
      zIndex: 10,
      backgroundColor: Colors.white,
    }),
  },
  headerSpacer: {
    ...(Platform.OS === "web" && {
      height: 80, // fallback until header onLayout runs
      width: "100%",
    }),
  },
  content: {
    flex: 1,
    // width: "100%",
  },
  scrollView: {
    flex: 1,
    ...(Platform.OS === 'web' && {
      minHeight: 0 as any,
      overflow: 'auto' as any,
      overflowX: 'hidden' as any,
    })
  },
  /** Step area when step owns its scroll (country, state, stateOfId, howTheyReceive). No overflow:auto so inner list scrolls. */
  stepAreaContainer: {
    flex: 1,
    minHeight: 0,
    ...(Platform.OS === 'web' && {
      overflow: 'hidden' as any,
      overflowX: 'hidden' as any,
    }),
  },
  /** When stepAreaHeight is not yet available (e.g. footer not measured), still constrain height on web so inner scroll works. */
  stepAreaContainerWebFallback: {
    height: '100%' as any,
    minHeight: '50vh' as any,
  },
  scrollContainer: {
    paddingBottom: 120, // space for footer (no flexGrow to avoid unnecessary scroll when content is short)
  },
  scrollContainerFraudPrevention: {
    paddingBottom: 32, // only 32px after content before footer
  },
  scrollContainerHeadToCashier: {
    paddingBottom: 32, // 32px space after HeadToCashier content before "I'm finished" button
  },
  // Review/final steps: paddingBottom is now computed dynamically from gradientFooterHeight
  mainWrapper: {
    flex: 1,
    flexDirection: "column",
    ...(Platform.OS === 'web' && {
      minHeight: 0 as any,
      overflow: 'hidden' as any,
    }),
  },
  footerContainer: {
    backgroundColor: Colors.white,
    paddingHorizontal: 24,
    paddingBottom: 24,
    paddingTop: 14,
  },
  footerShrink: {
    flexShrink: 0,
  },
  dropdownPriorityScrollLayer: {
    ...(Platform.OS === "web" && {
      position: "relative" as any,
      zIndex: 5,
      overflow: "visible" as any,
    }),
  },
  footerBehindDropdownLayer: {
    ...(Platform.OS === "web" && {
      position: "relative" as any,
      zIndex: 1,
    }),
  },
  button: {
    backgroundColor: Colors.black,
    paddingVertical: 16,
    borderRadius: 8,
    alignItems: "center",
    marginTop: 14,
    marginBottom: 0, // 24px gap comes from footerContainer paddingBottom
  },
  secondButton: {
    backgroundColor: Colors.white,
    paddingVertical: 16,
    borderRadius: 8,
    alignItems: "center",
    marginBottom: 0,
  },
  topBar: {
    flexDirection: "row",
    alignItems: "center",
    width: "100%",
    paddingVertical: 16,
    gap: 8,
  },
  topBarSide: {
    width: 48,
    flexShrink: 0,
    alignItems: "flex-start",
    justifyContent: "center",
  },
  topBarSideHidden: {
    width: 0,
    minWidth: 0,
    overflow: "hidden",
  },
  topBarCenter: {
    flex: 1,
    minWidth: 0,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 4,
  },
  topBarActions: {
    flexDirection: "row",
    alignItems: "center",
    flexShrink: 0,
    gap: 16,
  },
  topBarButton: {
    padding: 1,
  },
  topBarCountry: {
    flexDirection: "row",
    alignItems: "center",
    maxWidth: "100%",
    flexShrink: 1,
    paddingVertical: 8,
    paddingHorizontal: 8,
    backgroundColor: Colors.textInputBackground,
    borderRadius: 8,
  },
  topBarCountryFlag: {
    flexShrink: 0,
    marginRight: 6,
  },
  topBarCountryChevron: {
    flexShrink: 0,
    marginLeft: 4,
  },
  selectedCountryText: {
    flex: 1,
    minWidth: 0,
    fontFamily: "Rubik",
    fontSize: 12,
    fontWeight: "400",
    color: Colors.titleTextColor,
    lineHeight: 16,
  },
  IconStyle: {
    height: 24,
    width: 24
  },
  iconText: {
    fontSize: 20,
    color: "#111"
  },
  progressRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 16,
    height: 4,
  },
  progressSegment: {
    flex: 1,
    height: 4,
  },
  title: {
    fontSize: 20,
    fontWeight: "700",
    marginBottom: 16
  },
  buttonText: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.subTitleFont,
    fontWeight: '600', 
    color: Colors.white,
    lineHeight: 20,
  },
  modalOverlay: {
    flex: 1,
    justifyContent: "flex-end",
    backgroundColor: "rgba(0,0,0,0.4)"
  },
  root: {
    flex: 1,
    justifyContent: "flex-end",
    position: "fixed" as any, // web only
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  scrim: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "rgba(0,0,0,0.4)",
  },

  modalContent: {
    backgroundColor: Colors.white,
    padding: 24,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    width: "100%",
    zIndex: 2, // ensures it stays above scrim
  },
  // modalContent: {
  //   backgroundColor: "white",
  //   padding: 20,
  //   borderTopLeftRadius: 20,
  //   borderTopRightRadius: 20,
  // },
  accountLock: {
    fontSize: 24,
    fontWeight: "500",
    lineHeight: 28,
    fontFamily: 'Rubik',
    marginVertical: 26,
    color: Colors.accountLock,
  },
  accountLockInfo: {
    fontSize: 14,
    fontWeight: "400",
    lineHeight: 18,
    fontFamily: 'Rubik',
    marginBottom: 48,
    color: Colors.accountLock,
  },
  skipButton: {
    paddingVertical: 16,
    alignItems: "center"
  },
  skipText: {
    fontFamily: 'Rubik',
    fontSize: 16,
    fontWeight: "500",
    color: Colors.darkGrey,
    lineHeight: 24,
  },
  modalHeaderRow: {
    alignSelf: 'flex-end',
    marginBottom: 48
  },
  modalTitle: {
    fontFamily: 'Rubik',
    fontSize: 24,
    fontWeight: "500",
    color: Colors.accountLock,
    lineHeight: 28,
    marginVertical: 26
  },
  modalInfo: {
    fontFamily: 'Rubik',
    fontSize: 14,
    fontWeight: "400",
    color: Colors.accountLock,
    lineHeight: 18,
    marginBottom: 26,
    textAlign: 'center',
    paddingHorizontal: 10
  },
  bottomSheet: {
    backgroundColor: Colors.white,
    padding: 24,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
  },
  modalDOB: {
    fontFamily: 'Rubik',
    fontSize: 32,
    fontWeight: "500",
    color: Colors.accountLock,
    textAlign: 'center',
    // marginTop: 10,
    marginBottom: 48,
  },
  /** Full-width so long first/last names wrap; centered with sheet horizontal padding */
  receiverLegalNameContainer: {
    alignSelf: 'stretch',
    width: '100%',
    paddingHorizontal: 8,
    marginBottom: 48,
  },
  receiverLegalNameText: {
    fontFamily: 'Rubik',
    fontSize: 32,
    fontWeight: '500',
    color: Colors.accountLock,
    textAlign: 'center',
    width: '100%',
  },
  confirmBtn: {
    backgroundColor: "#000",
    padding: 14,
    borderRadius: 8,
    alignItems: "center",
    height: 48
  },
  footer: {
    // paddingHorizontal: 24,
    paddingBottom: Platform.OS === "ios" ? 24 : 16,
    backgroundColor: Colors.white
  },
  cancelBtn: { paddingTop: 24, alignItems: "center" },
  cancelText: {
    fontFamily: 'Rubik',
    fontSize: 16,
    fontWeight: "500",
    color: Colors.darkGrey,
    lineHeight: 24,
  },
  contniueAsGuest: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.subTitleFont,
    fontWeight: '500',
    lineHeight: 20,
    color: Colors.titleTextColor,
  },
  gradientFooter: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 1000,
    backgroundColor: Colors.white,
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    ...(Platform.OS === 'web' && {
      position: 'fixed' as any,
      bottom: 0,
      left: 0,
      right: 0,
      width: '100%' as any,
      margin: 0 as any,
      boxSizing: 'border-box' as any,
      // Prevent gap above browser chrome on iPhone Safari / mobile
      paddingBottom: 'env(safe-area-inset-bottom, 0px)' as any,
    })
  },
  viewTap: {
    height: 4,
    width: 77,
    backgroundColor: Colors.titleTextColor,
    borderRadius:24,
    // marginBottom: 24,
    alignSelf: 'center'
  },
  gradientContent: {
    paddingHorizontal: 24,
    // paddingVertical: 20,
    // alignItems: "center",
  },
  gradientRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
    marginBottom: 16,
  },
  gradientFeeLabel: {
    fontSize: 14,
    fontFamily: 'Rubik',
    fontWeight: "400",
    lineHeight: 22,
    color: Colors.titleTextColor,
  },
  gradientPrice: {
    fontSize: 16,
    fontFamily: 'Rubik',
    fontWeight: '500',
    lineHeight: 24,
    color: Colors.titleTextColor,
  },
  gradientContinueButton: {
    width: "100%",
    backgroundColor: "#000",
    borderRadius: 8,
    paddingVertical: 16,
    alignItems: "center",
    shadowColor: "#000",
    marginBottom: 32
  },
  gradientContinueButtonText: {
    fontFamily: 'Rubik',
    fontSize: 16,
    fontWeight: "500",
    color: Colors.white,
  },
  oldPrice: {
    fontSize: 16,
    fontFamily: 'Rubik',
    // textDecorationLine: "line-through",
    marginRight: 8,
  },
  newPrice: {
    fontSize: 16,
    fontFamily: 'Rubik',
    fontWeight: "bold",
    color: "green",
  },
 
});